/**
 * AssetProfilePage.jsx
 * Full-page asset form (create + edit) AND view profile with all tabs.
 * Route: /assets/new          → create mode  (no id param)
 * Route: /assets/:id/profile  → view mode    (has id param)
 */
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  assetsAPI, mastersAPI, sparesAPI, workOrdersAPI, usersAPI, companyAPI,
} from '../../api';
import AssetQRModal from './AssetQRModal';
import toast from 'react-hot-toast';

/* ── tiny style helpers ─────────────────────────────────────── */
const IS = {                                    // base input style
  width:'100%', border:'1.5px solid #e2e8f0', borderRadius:7,
  padding:'8px 11px', fontSize:13, outline:'none', boxSizing:'border-box',
  background:'#fff', color:'#1a1f2e', transition:'border .15s',
};
const LS = { fontSize:12, fontWeight:600, color:'#475569', display:'block', marginBottom:4 };
const SECT = {
  fontSize:11, fontWeight:700, color:'rgba(26,31,46,.38)', textTransform:'uppercase',
  letterSpacing:'.07em', marginBottom:10, marginTop:22, paddingBottom:6,
  borderBottom:'1px solid #e2e8f0', display:'flex', alignItems:'center', gap:6,
};
const G2 = { display:'grid', gridTemplateColumns:'1fr 1fr',     gap:'12px 18px' };
const G3 = { display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px 18px' };
const Hidecss = { display:'none'};
const showDepreciationSection = false;
const SC = {
  operational:{ bg:'#dcfce7', c:'#15803d' }, maintenance:{ bg:'#fef3c7', c:'#b45309' },
  breakdown:  { bg:'#fee2e2', c:'#dc2626' }, retired:    { bg:'#f1f5f9', c:'#64748b' },
};
const fmt  = n => n!=null ? `₹${Number(n).toLocaleString('en-IN')}` : '—';
const fdt  = d => d ? String(d).slice(0,10) : '—';

/* ── tiny reusable components ──────────────────────────────── */
function Inp({ value, onChange, type='text', placeholder='', readOnly=false, mono=false, col, ...p }) {
  return (
    <div style={{ gridColumn: col }}>
      {p.label && <label style={LS}>{p.label}</label>}
      <input
        className="ap-inp"
        type={type} value={value ?? ''} readOnly={readOnly}
        onChange={onChange} placeholder={placeholder}
        style={{ ...IS, fontFamily: mono ? "'Courier New',monospace" : 'inherit' }}
      />
    </div>
  );
}
function Sel({ value, onChange, options=[], placeholder='— Select —', label, col, disabled=false }) {
  return (
    <div style={{ gridColumn: col }}>
      {label && <label style={LS}>{label}</label>}
      <select className="ap-inp" value={value ?? ''} onChange={onChange} disabled={disabled}
        style={{ ...IS }}>
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
      </select>
    </div>
  );
}
function DateIn({ value, onChange, label, col }) {
  return (
    <div style={{ gridColumn: col }}>
      {label && <label style={LS}>{label}</label>}
      <input className="ap-inp" type="date" value={value ?? ''} onChange={onChange}
        style={{ ...IS }} />
    </div>
  );
}
function NumIn({ value, onChange, label, col, placeholder='0' }) {
  return (
    <div style={{ gridColumn: col }}>
      {label && <label style={LS}>{label}</label>}
      <input className="ap-inp" type="number" value={value ?? ''} onChange={onChange}
        placeholder={placeholder} style={{ ...IS }} />
    </div>
  );
}
function SectionHeader({ children }) {
  return <div style={SECT}>{children}<div style={{ flex:1, height:1, background:'#e2e8f0', marginLeft:4 }}/></div>;
}
function InfoRow({ label, value, mono, accent }) {
  if (value == null || value === '') return null;
  return (
    <tr>
      <td style={{ padding:'6px 0', color:'#64748b', fontSize:13, width:'45%', verticalAlign:'top', paddingRight:8 }}>{label}</td>
      <td style={{ padding:'6px 0', fontWeight: accent?700:500, fontSize:13,
        color: accent?'#1a56db':'inherit', fontFamily: mono?"'Courier New',monospace":'inherit' }}>{value}</td>
    </tr>
  );
}
function Chip({ bg, c, children }) {
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'3px 10px',
      borderRadius:20, fontSize:11, fontWeight:600, background:bg, color:c }}>
      <span style={{ width:5, height:5, borderRadius:'50%', background:c }}/>
      {children}
    </span>
  );
}
function ActionBtn({ color='outline', onClick, children, disabled }) {
  const styles = {
    blue:    { background:disabled?'#93c5fd':'#1a56db', color:'#fff',    border:'none' },
    red:     { background:'#fee2e2', color:'#dc2626', border:'1px solid rgba(220,38,38,.2)' },
    teal:    { background:'#dbeafe', color:'#1a56db', border:'1px solid rgba(26,86,219,.2)' },
    green:   { background:'#dcfce7', color:'#15803d', border:'1px solid rgba(21,128,61,.2)' },
    outline: { background:'transparent', color:'#64748b', border:'1px solid #cbd5e1' },
  };
  return (
    <button onClick={onClick} disabled={!!disabled} style={{
      ...styles[color]||styles.outline, padding:'7px 14px', borderRadius:7, fontSize:12,
      fontWeight:500, cursor:disabled?'not-allowed':'pointer',
      display:'inline-flex', alignItems:'center', gap:5,
    }}>{children}</button>
  );
}

/* ── TABS ───────────────────────────────────────────────────── */
const FORM_TABS = [
  { id:'overview',   label:'Overview' },
  { id:'purchase',   label:'Purchase & Supplier' },
  { id:'warranty',   label:'Warranty & Insurance' },
  { id:'finance',    label:'Finance' },
];
const VIEW_TABS = [
  { id:'overview',   label:'Overview' },
  { id:'purchase',   label:'Purchase & Supplier' }, 
  { id:'finance',    label:'Financial' },
  { id:'documents',  label:'Documents' },
  { id:'spareparts', label:'Spare Parts' },
  { id:'tickets',    label:'Tickets / WOs' },
  { id:'history',    label:'Full History' },
];

/* ── blank form ─────────────────────────────────────────────── */
const BLANK = () => ({
  name:'', asset_code:'', category_id:'', location_id:'', department:'',
  status:'operational', make:'', model:'', serial_number:'',
  description:'', is_critical:false, assigned_to:'', employee_id:'',
  plant_status:'', finance_status:'Active',
  vendor_id:'', po_number:'',
  our_invoice_no:'', our_invoice_date:'',
  supplier_invoice_no:'', supplier_invoice_date:'',
  purchase_date:'', delivery_date:'', installation_date:'', commission_date:'',
  purchase_cost:'', gst_percent:'', gst_amount:'',
  installation_cost:'', transport_cost:'',
  subsequent_cost_transport:'', subsequent_cost_installation:'',
  payment_terms:'', payment_status:'', capex_code:'', approved_by:'',
  warranty_period:'', warranty_start_date:'', warranty_end_date:'',
  valid_insurance:'N', valid_insurance_from:'', valid_insurance_to:'',
  valid_amc:'N', valid_amc_from:'', valid_amc_to:'',
  amc_contract:'', amc_start:'', amc_end:'', amc_cost:'',
  dep_type_id:'', depreciation_method:'slm', useful_life_years:'10', residual_value:'',
});

const PLANT_STATUS_OPTS = ['In Service','Under Maintenance','Idle','Scrapped','Transferred','Standby'];
const PAYMENT_STATUS_OPTS = ['Fully Paid','Partially Paid','Pending','Credit'];
 
/* ══════════════════════════════════════════════════════════════ */
export default function AssetProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  /* state */
  const [profile,  setProfile]  = useState(null);
  const [pageLoading, setPageLoading] = useState(!isNew);
  const [editMode, setEditMode] = useState(isNew);
  const [saving,   setSaving]   = useState(false);
  const [tab,      setTab]      = useState('overview');
  const [qrOpen,   setQrOpen]   = useState(false);
  const [woModal,  setWoModal]  = useState(null);
  const [users,    setUsers]    = useState([]);
  const [failureTypes, setFailureTypes] = useState([]);
  const [frequencies,     setFrequencies]     = useState([]);
  const [maintenanceTypes, setMaintenanceTypes] = useState([]);

  /* masters */
  const [categories,   setCategories]   = useState([]);
  const [locations,    setLocations]    = useState([]);
  const [departments,  setDepartments]  = useState([]);
  const [employees,    setEmployees]    = useState([]);
  const [depMethods,   setDepMethods]   = useState([]);
  const [depTypes,     setDepTypes]     = useState([]);
  const [vendors,      setVendors]      = useState([]);
  /* new masters */
  const [assetClasses,    setAssetClasses]    = useState([]);
  const [warrantyPeriods, setWarrantyPeriods] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);

  /* financial year review */
  const currentFY = (() => {
    const now = new Date(), yr = now.getFullYear();
    return now.getMonth() >= 3
      ? `${yr}-${String(yr + 1).slice(-2)}`
      : `${yr - 1}-${String(yr).slice(-2)}`;
  })();
  const [finValues, setFinValues] = useState([]);
  const [selFY,     setSelFY]     = useState(currentFY);   // ← defaults to current FY
  const [fyForm,    setFYForm]    = useState({});
  const [savingFY,  setSavingFY]  = useState(false);

  /* subsequent costs (multi-row) */
  const [subsequentCosts, setSubsequentCosts] = useState([]);

  /* spare parts */
  const [allSpares,   setAllSpares]   = useState([]);
  const [spareSearch, setSpareSearch] = useState('');
  const [linking,     setLinking]     = useState(false);
  const [showDrop,    setShowDrop]    = useState(false);

  /* documents */
  const [docTitle,  setDocTitle]  = useState('');
  const [docType,   setDocType]   = useState('manual');
  const [docFile,   setDocFile]   = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const imgRef  = useRef(null);

  /* image upload (edit mode) */
  const [imageFile,    setImageFile]    = useState(null);
  const [imagePreview, setImagePreview] = useState(null);  // existing server URL
  const [localPreview, setLocalPreview] = useState(null);  // new file blob URL

  /* form */
  const [form, setForm] = useState(BLANK());
  const s = (k, v) => setForm(f => ({ ...f, [k]: v }));

  /* ── load profile ───────────────────────────────────────── */
  const loadProfile = async () => {
    if (isNew) return;
    setPageLoading(true);
    try {
      const r = await assetsAPI.profile(id);
      const p = r.data;
      setProfile(p);
      // Set image preview from saved image
      const imgSrc = p.image_url || (p.image ? (p.image.startsWith('http') ? p.image
        : `${(process.env.REACT_APP_API_URL||'http://localhost:8000/api').replace('/api','')}${p.image}`) : null);
      if (imgSrc) setImagePreview(imgSrc); else setImagePreview(null);
      setLocalPreview(null);
      setForm({
        name:p.name||'', asset_code:p.asset_code||'',
        category_id:p.category_id||'', location_id:p.location_id||'',
        department:p.department||'', status:p.status||'operational',
        make:p.make||'', model:p.model||'', serial_number:p.serial_number||'',
        description:p.description||'', is_critical:!!p.is_critical,
        assigned_to:p.assigned_to||'', employee_id:p.employee_id||'',
        plant_status:p.plant_status||'', finance_status:p.finance_status||'Active',
        vendor_id:p.vendor?.id||'', po_number:p.po_number||'',
        our_invoice_no:p.our_invoice_no||'', our_invoice_date:p.our_invoice_date||'',
        supplier_invoice_no:p.supplier_invoice_no||'', supplier_invoice_date:p.supplier_invoice_date||'',
        purchase_date:p.purchase_date||'', delivery_date:p.delivery_date||'',
        installation_date:p.installation_date||'', commission_date:p.commission_date||'',
        purchase_cost:p.purchase_cost||'', gst_percent:p.gst_percent||'',
        gst_amount:p.gst_amount||'', installation_cost:p.installation_cost||'',
        transport_cost:p.transport_cost||'',
        subsequent_cost_transport:p.subsequent_cost_transport||'',
        subsequent_cost_installation:p.subsequent_cost_installation||'',
        payment_terms:p.payment_terms||'', payment_status:p.payment_status||'',
        capex_code:p.capex_code||'', approved_by:p.approved_by||'',
        warranty_period:p.warranty_period||'',
        warranty_start_date:p.warranty_start_date||'',
        warranty_end_date:p.warranty_end_date||'',
        valid_insurance:p.valid_insurance||'N',
        valid_insurance_from:p.valid_insurance_from||'',
        valid_insurance_to:p.valid_insurance_to||'',
        valid_amc:p.valid_amc||'N',
        valid_amc_from:p.valid_amc_from||'', valid_amc_to:p.valid_amc_to||'',
        amc_contract:p.amc_contract||'', amc_start:p.amc_start||'',
        amc_end:p.amc_end||'', amc_cost:p.amc_cost||'',
        dep_type_id:p.dep_type_id||'',
        installation_cost_head_yn:p.installation_cost_head_yn||'N',
        transport_cost_head_yn:p.transport_cost_head_yn||'N',
        asset_class_id:String(p.asset_class_id||''),
        capitalized_date:p.capitalized_date||'',
        warranty_period_id:String(p.warranty_period_id||''),
        depreciation_method:p.depreciation_method||'slm',
        useful_life_years:p.useful_life_years||'10',
        residual_value:p.residual_value||'',
      });
      // Load financial values
      // Load subsequent costs
      assetsAPI.listSubsequentCosts(id).then(r => setSubsequentCosts(r.data||[])).catch(()=>{});
      mastersAPI.listFinancialValues(id).then(r => {
        const vals = r.data || [];
        setFinValues(vals);

        /* Always default to current FY in Financial Year Review dropdown */
        const curRow = vals.find(v => v.financial_year === currentFY);
        if (curRow) {
          setSelFY(currentFY);
          setFYForm(curRow);
        } else {
          setSelFY(currentFY);
          setFYForm({
            financial_year:       currentFY,
            acquisition_cost:     p.purchase_cost || '',
            planned_depreciation: '',
            posted_depreciation:  '',
            nbv_year_end:         '',
            nbv_current_month:    '',
          });
        }
      }).catch(()=>{});
    } catch { toast.error('Failed to load asset'); }
    finally { setPageLoading(false); }
  };

  /* ── load masters ───────────────────────────────────────── */
  const loadMasters = () => {
    assetsAPI.categories().then(r => setCategories(r.data||[])).catch(()=>{});
    // locations from company
    mastersAPI.listLocations().then(r => setLocations(r.data||[])).catch(()=>{});    // correct FK table
    // departments from company
    companyAPI.listDepartments().then(r => setDepartments(r.data?.data||r.data||[])).catch(()=>{});
    // employees
    try { mastersAPI.listEmployees().then(r => setEmployees(r.data||[])).catch(()=>{}); } catch {}
    // dep methods & types
    mastersAPI.listDepMethods().then(r => setDepMethods(r.data||[])).catch(()=>{});
    mastersAPI.listDepTypes().then(r => setDepTypes(r.data||[])).catch(()=>{});
    mastersAPI.listAssetClasses().then(r => setAssetClasses(r.data||[])).catch(()=>{});
    mastersAPI.listWarrantyPeriods().then(r => setWarrantyPeriods(r.data||[])).catch(()=>{});
    mastersAPI.listFinancialYears?.().then(r => setFinancialYears(r.data||[])).catch(()=>{});
    // vendors
    import('../../api').then(({ supplyAPI }) => {
      supplyAPI.listVendors && supplyAPI.listVendors()
        .then(r => setVendors(Array.isArray(r.data)?r.data:(r.data?.data||[])))
        .catch(()=>{});
    }).catch(()=>{});
  };

  useEffect(() => {
    setTab('overview');
    loadMasters();
    loadProfile();
    if (!isNew) {
      sparesAPI.list({ limit:200 }).then(r => setAllSpares(r.data?.data||[])).catch(()=>{});
    }
  }, [id]);

  /* ── save ───────────────────────────────────────────────── */
  const handleSave = async () => {
    if (!form.name.trim())       { toast.error('Asset Name is required');  return; }
    if (!form.asset_code.trim()) { toast.error('Asset Code is required');  return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v]) => {
        if (v !== '' && v !== null && v !== undefined) fd.append(k, v);
      });
      if (imageFile) fd.append('image', imageFile);
      if (isNew) {
        const r = await assetsAPI.create(fd);
        toast.success('Asset created successfully!');
        navigate(`/assets/${r.data.id}/profile`);
      } else {
        await assetsAPI.update(id, fd);
        toast.success('Asset updated successfully!');
        await loadProfile();
        setEditMode(false);
      }
    } catch(e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  /* ── spare helpers ──────────────────────────────────────── */
  const linkSpare = async spId => {
    setLinking(true);
    try { await assetsAPI.linkSpare(id,spId); await loadProfile(); setSpareSearch(''); setShowDrop(false); toast.success('Linked'); }
    catch { toast.error('Failed to link'); } finally { setLinking(false); }
  };
  const unlinkSpare = async spId => {
    try { await assetsAPI.unlinkSpare(id,spId); setProfile(p=>({...p,spare_parts:p.spare_parts.filter(s=>s.id!==spId)})); toast.success('Removed'); }
    catch { toast.error('Failed'); }
  };

  /* ── doc helpers ────────────────────────────────────────── */
  const uploadDoc = async () => {
    if (!docTitle.trim()) { toast.error('Enter a title'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title',docTitle); fd.append('doc_type',docType);
      if (docFile) fd.append('file',docFile);
      await assetsAPI.uploadDoc(id,fd);
      setDocTitle(''); setDocFile(null); setDocType('manual');
      await loadProfile(); toast.success('Uploaded');
    } catch { toast.error('Upload failed'); } finally { setUploading(false); }
  };
  const deleteDoc = async docId => {
    if (!window.confirm('Delete this document?')) return;
    try { await assetsAPI.deleteDoc(id,docId); setProfile(p=>({...p,documents:p.documents.filter(d=>d.id!==docId)})); toast.success('Deleted'); }
    catch { toast.error('Failed'); }
  };

  /* ── compute financials ─────────────────────────────────── */
  const calcFin = (overrideForm) => {
    const src = overrideForm || form;
    const cost  = parseFloat(src.purchase_cost)||0;
    const inst  = parseFloat(src.installation_cost)||0;
    const trans = parseFloat(src.transport_cost)||0;
    const subT  = parseFloat(src.subsequent_cost_transport)||0;
    const subI  = parseFloat(src.subsequent_cost_installation)||0;
    const total = cost + inst + trans + subT + subI;
    const life  = parseInt(src.useful_life_years)||10;
    const resid = parseFloat(src.residual_value) || total * 0.10;
    const annDep = life>0 ? (total-resid)/life : 0;
    /* Use capitalized_date first, fall back to purchase_date */
    const refDate = src.capitalized_date || src.purchase_date;
    const py = refDate ? new Date(refDate).getFullYear() : null;
    const pm = refDate ? new Date(refDate).getMonth() : null; // 0-indexed
    /* FY start year: if month >= 3 (April+), FY starts this year; else last year */
    const capFYStart = py != null ? (pm >= 3 ? py : py - 1) : null;
    const cy = new Date().getFullYear();
    const now = new Date();
    const curFYStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear()-1;
    const yu = capFYStart != null ? Math.max(0, curFYStart - capFYStart) : 0;
    const accDep  = Math.min(annDep*yu, total-resid);
    const bookVal = Math.max(resid, total-accDep);
    const depRate = total>0 ? ((annDep/total)*100).toFixed(1) : '0.0';
    const eolFYStart = capFYStart != null ? capFYStart + life : null;
    const eol = eolFYStart ? `${eolFYStart}-${String(eolFYStart+1).slice(-2)}` : '—';
    return { cost, inst, trans, subT, subI, total, life, resid, annDep, py, pm, capFYStart, yu, accDep, bookVal, depRate, eol };
  };

  /* ── dep type info ──────────────────────────────────────── */
  const selDepType = depTypes.find(dt => dt.id === parseInt(form.dep_type_id));
  const selMethodCode = selDepType
  ? (depMethods.find(m=>m.id===parseInt(selDepType.method_id))?.code || selDepType.method_code || '')
  : '';

  /* Auto-calc Salvage Value from Capitalised Total × Salvage % */
  const calcCapitalisedTotal = () => {
    const base  = parseFloat(form.purchase_cost)||0;
    const inst  = form.installation_cost_head_yn==='Y' ? (parseFloat(form.installation_cost)||0) : 0;
    const trans = form.transport_cost_head_yn==='Y'    ? (parseFloat(form.transport_cost)||0)    : 0;
    const sub   = (subsequentCosts||[]).filter(sc=>sc.include_in_cost_head==='Y')
      .reduce((t,sc)=>(t+(parseFloat(sc.transport_cost)||0)+(parseFloat(sc.installation_cost)||0)),0);
    return base + inst + trans + sub;
  };
  const autoFillSalvage = (depTypeId) => {
    const dt = depTypes.find(d => d.id === parseInt(depTypeId));
    if (!dt?.salvage_value_pct) return;
    const total = calcCapitalisedTotal();
    if (total > 0) s('residual_value', (total * dt.salvage_value_pct / 100).toFixed(2));
  };
  /* ── loading ────────────────────────────────────────────── */
  if (pageLoading) return (
    <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'60vh' }}>
      <div style={{ textAlign:'center' }}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ width:36,height:36,border:'3px solid #e2e8f0',borderTopColor:'#1a56db',
          borderRadius:'50%',animation:'spin .8s linear infinite',margin:'0 auto 12px' }}/>
        <div style={{ fontSize:13,color:'#64748b' }}>Loading…</div>
      </div>
    </div>
  );

  if (!isNew && !profile) return (
    <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'60vh' }}>
      <div style={{ textAlign:'center',color:'#94a3b8' }}>
        <div style={{ fontSize:48,marginBottom:12 }}>🔍</div>
        <div style={{ fontSize:16,fontWeight:600,color:'#1e2a3b',marginBottom:12 }}>Asset not found</div>
        <button onClick={()=>navigate('/assets')} style={{ padding:'8px 18px',background:'#1a56db',
          color:'#fff',border:'none',borderRadius:7,cursor:'pointer',fontSize:13 }}>← Back to Assets</button>
      </div>
    </div>
  );

  /* derived */
  const fin  = calcFin(editMode ? form : (profile ? { ...profile } : form));
  /* Override depRate with the percentage from Depreciation Type Master */
  if (selDepType?.percentage != null) {
    fin.depRate = parseFloat(selDepType.percentage).toFixed(1);
  }
  const sc   = SC[profile?.status] || SC.retired;
  const wos  = profile?.work_orders   || [];
  const pms  = profile?.pm_schedules  || [];
  const sprs = profile?.spare_parts   || [];
  const docs = profile?.documents     || [];
  const tabs = editMode ? FORM_TABS : VIEW_TABS;
  const linkedIds = new Set(sprs.map(s=>s.id));
  const filtSpares = allSpares.filter(s => !linkedIds.has(s.id) && spareSearch.trim().length>=3 &&
    (s.name.toLowerCase().includes(spareSearch.toLowerCase()) ||
     s.part_number.toLowerCase().includes(spareSearch.toLowerCase())));
  const nowLabel = new Date().toLocaleString('en-IN',{month:'short',year:'numeric'});

  /* Warranty helpers — used by overview, purchase & history tabs */
const wpName  = profile
? (profile.warranty_period
   || warrantyPeriods.find(wp => wp.id === parseInt(profile.warranty_period_id))?.warranty_name
   || '')
: '';
const wEnd    = profile ? (profile.warranty_end_date || profile.warranty_expiry || '') : '';
const wStart  = profile ? (profile.warranty_start_date || '') : '';
const wActive = wEnd ? new Date(wEnd) >= new Date() : null;

  /* ══════════════ RENDER ════════════════════════════════════ */
  return (
    <div style={{ background:'#f8fafc', minHeight:'100vh' }}>
      <style>{`
        @keyframes spin { to { transform:rotate(360deg); } }
        .ap-inp { transition: border-color .15s, box-shadow .15s; }
        .ap-inp:focus { border-color:#1a56db !important; box-shadow:0 0 0 3px rgba(26,86,219,.1); outline:none; }
        .ap-tab:hover { background:#f1f5f9; }
      `}</style>

      {/* ── BREADCRUMB ─────────────────────────────────────── */}
      <div style={{ background:'#fff', borderBottom:'1px solid #e2e8f0', padding:'7px 24px',
        display:'flex', alignItems:'center', gap:8, fontSize:13 }}>
        <button onClick={()=>navigate('/assets')} style={{ background:'none', border:'1px solid #e2e8f0',
          borderRadius:5, padding:'3px 10px', cursor:'pointer', color:'#64748b', fontSize:12 }}>← Assets</button>
        <span style={{ color:'#cbd5e1' }}>/</span>
        {isNew
          ? <span style={{ fontWeight:600, color:'#1a1f2e' }}>New Asset</span>
          : <>
            <span style={{ color:'#64748b' }}>{profile?.asset_code}</span>
            <span style={{ color:'#cbd5e1' }}>/</span>
            <span style={{ fontWeight:600, color:'#1a1f2e' }}>{profile?.name}</span>
          </>}
      </div>

      {/* ── PAGE HEADER ────────────────────────────────────── */}
      <div style={{ background:'#fff', borderBottom:'1px solid #e2e8f0', padding:'12px 24px' }}>

        {/* Row 1: asset identity */}
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom: (!isNew && !editMode) ? 10 : 0 }}>
          <div style={{ width:46, height:46, background:isNew?'#dbeafe':'#f0f6ff', borderRadius:10,
            display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0, overflow:'hidden' }}>
            {!isNew && (localPreview || imagePreview || profile?.image_url)
              ? <img src={localPreview || imagePreview || profile?.image_url} alt=""
                  style={{ width:46, height:46, objectFit:'cover' }}/>
              : (isNew ? '➕' : '⚙')}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:17, fontWeight:700, color:'#1a1f2e', lineHeight:1.3,
              whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
              {isNew ? 'Register New Asset' : profile?.name}
            </div>
            <div style={{ fontSize:12, color:'#64748b', marginTop:2, display:'flex', gap:8, flexWrap:'wrap' }}>
              {!isNew && profile?.location_name && <span>📍 {profile.location_name}</span>}
              {!isNew && profile?.make && <span>· {profile.make}{profile.model ? ' ' + profile.model : ''}</span>}
              {!isNew && profile?.serial_number && (
                <span style={{ fontFamily:'monospace', color:'#1a56db' }}>· {profile.serial_number}</span>
              )}
              {isNew && <span style={{ color:'#94a3b8' }}>Fill in all tabs, then save</span>}
            </div>
          </div>
          {!isNew && profile?.status && (
            <Chip bg={sc.bg} c={sc.c}>{profile.status.toUpperCase()}</Chip>
          )}
          {!isNew && profile?.is_critical && (
            <Chip bg="#fef3c7" c="#d97706">⚡ CRITICAL</Chip>
          )}
          {/* Edit / Save buttons always visible on the right */}
          {editMode && (
            <div style={{ display:'flex', gap:8, marginLeft:'auto', flexShrink:0 }}>
              <ActionBtn color="outline" onClick={()=>{ if(isNew){navigate('/assets');}else{loadProfile();setEditMode(false);setTab('overview');} }}>
                ✕ {isNew ? 'Cancel' : 'Discard'}
              </ActionBtn>
              <ActionBtn color="blue" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : isNew ? '✓ Create Asset' : '✓ Save Changes'}
              </ActionBtn>
            </div>
          )}
        </div>

        {/* Row 2: action buttons (view mode only) — wraps naturally */}
        {!editMode && !isNew && (
          <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
            <ActionBtn color="red"
              onClick={()=>{setWoModal('breakdown'); usersAPI.list().then(r=>setUsers(r.data?.data||r.data||[])).catch(()=>{}); mastersAPI.listFrequencies().then(r=>setFrequencies(r.data||[])).catch(()=>{}); mastersAPI.listMaintenanceTypes().then(r=>setMaintenanceTypes(r.data||[])).catch(()=>{}); mastersAPI.listFailureTypes().then(r=>setFailureTypes(r.data||[])).catch(()=>{})}}>
              ⚡ Report Breakdown
            </ActionBtn>
            <ActionBtn color="teal"
              onClick={()=>{setWoModal('pm'); usersAPI.list().then(r=>setUsers(r.data?.data||r.data||[])).catch(()=>{}); mastersAPI.listFrequencies().then(r=>setFrequencies(r.data||[])).catch(()=>{}); mastersAPI.listMaintenanceTypes().then(r=>setMaintenanceTypes(r.data||[])).catch(()=>{}); mastersAPI.listFailureTypes().then(r=>setFailureTypes(r.data||[])).catch(()=>{})}}>
              🔧 Schedule PM
            </ActionBtn>
            <ActionBtn color="outline"
              onClick={()=>{setWoModal('inspection'); usersAPI.list().then(r=>setUsers(r.data?.data||r.data||[])).catch(()=>{}); mastersAPI.listFrequencies().then(r=>setFrequencies(r.data||[])).catch(()=>{}); mastersAPI.listMaintenanceTypes().then(r=>setMaintenanceTypes(r.data||[])).catch(()=>{}); mastersAPI.listFailureTypes().then(r=>setFailureTypes(r.data||[])).catch(()=>{})}}>
              🔍 Request Inspection
            </ActionBtn>
            <ActionBtn color="outline"
              onClick={()=>{setWoModal('repair'); usersAPI.list().then(r=>setUsers(r.data?.data||r.data||[])).catch(()=>{}); mastersAPI.listFrequencies().then(r=>setFrequencies(r.data||[])).catch(()=>{}); mastersAPI.listMaintenanceTypes().then(r=>setMaintenanceTypes(r.data||[])).catch(()=>{}); mastersAPI.listFailureTypes().then(r=>setFailureTypes(r.data||[])).catch(()=>{})}}>
              ✱ Raise Repair WO
            </ActionBtn>
            <div style={{ flex:1 }}/>
            <ActionBtn color="outline" onClick={()=>setQrOpen(true)}>📱 QR Label</ActionBtn>
            <ActionBtn color="blue" onClick={()=>{ setEditMode(true); setTab('overview'); }}>✏ Edit Asset</ActionBtn>
          </div>
        )}
      </div>

      {/* ── KPI STRIP (view mode only) ──────────────────────── */}
      {!isNew && !editMode && (
        <div style={{ background:'#fff', borderBottom:'1px solid #e2e8f0', padding:'10px 24px',
          display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:10 }}>
          {[
            { label:'Work Orders',  val:wos.length,                              col:'#1a56db', bg:'#dbeafe' },
            { label:'Breakdowns',   val:wos.filter(w=>w.type==='breakdown').length, col:'#dc2626', bg:'#fee2e2' },
            { label:'PM Schedules', val:pms.length,                              col:'#0d9488', bg:'#ccfbf1' },
            { label:'Linked Spares',val:sprs.length,                             col:'#d97706', bg:'#fef3c7' },
            { label:'Documents',    val:docs.length,                             col:'#7c3aed', bg:'#ede9fe' },
          ].map(k => (
            <div key={k.label} style={{ background:k.bg, borderRadius:10, padding:'10px 14px', textAlign:'center' }}>
              <div style={{ fontSize:22, fontWeight:800, color:k.col }}>{k.val}</div>
              <div style={{ fontSize:10, color:'#64748b', marginTop:2 }}>{k.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── TABS ─────────────────────────────────────────────── */}
      <div style={{ background:'#fff', borderBottom:'1px solid #e2e8f0',
        padding:'0 24px', display:'flex', overflowX:'auto' }}>
        {tabs.map(t => (
          <button key={t.id} className="ap-tab" onClick={()=>setTab(t.id)} style={{
            padding:'11px 16px', fontSize:13,
            fontWeight: tab===t.id ? 600 : 400,
            color: tab===t.id ? '#1a56db' : '#64748b',
            borderBottom: tab===t.id ? '2px solid #1a56db' : '2px solid transparent',
            background:'none', border:'none', borderRadius:0, cursor:'pointer',
            whiteSpace:'nowrap', marginBottom:-1, transition:'all .15s',
          }}>{t.label}</button>
        ))}
        {/* save hint in edit mode */}
        {editMode && (
          <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', padding:'0 4px' }}>
            <span style={{ fontSize:11, color:'#94a3b8' }}>Fill all tabs then Save</span>
          </div>
        )}
      </div>

      {/* ═══════ BODY ══════════════════════════════════════════ */}
      <div style={{ padding:'20px 24px', maxWidth:1280 }}>

        {/* ╔══ OVERVIEW ═════════════════════════════════════╗ */}
        {tab==='overview' && editMode && (
          <div style={{ display:'grid',  gap:28 }}> {/* gridTemplateColumns:'1fr 1fr', */}
            {/* LEFT */}
            <div>
              <SectionHeader>Basic Information</SectionHeader>
              <div style={G2}>
              <Inp label="Asset Code *" value={form.asset_code} onChange={e=>s('asset_code',e.target.value.toUpperCase())} placeholder="AST-CNC-001" mono/>
                <Inp label="Asset Name *"  value={form.name} onChange={e=>s('name',e.target.value)} placeholder="e.g. CNC Machining Centre #1"/>
                <Sel label="Status" value={form.status} onChange={e=>s('status',e.target.value)}
                  options={['operational','maintenance','breakdown','retired'].map(v=>({value:v,label:v.charAt(0).toUpperCase()+v.slice(1)}))}/>
                <Sel label="Category" value={form.category_id} onChange={e=>s('category_id',e.target.value)}
                  options={categories.map(c=>({value:c.id,label:c.name}))} placeholder="— Select Category —"/>
                <Sel label="Location" value={form.location_id} onChange={e=>s('location_id',e.target.value)}
                  options={locations.map(l=>({value:l.id,label:l.location_name||l.name}))} placeholder="— Select Location —"/>
                <Sel label="Department" value={form.department} onChange={e=>s('department',e.target.value)}
                  options={departments.map(d=>({value:d.department_name||d.name,label:d.department_name||d.name}))} placeholder="— Select Dept —"/>
                <Sel label="Assigned Employee" value={form.employee_id} onChange={e=>s('employee_id',e.target.value)} 
                  options={employees.map(e=>({value:e.id,label:`${e.name}${e.designation?' — '+e.designation:''}`}))} placeholder="— Select Employee —"/>
                 <Inp label="Serial Number" value={form.serial_number} onChange={e=>s('serial_number',e.target.value)} mono/>
                <Inp label="Make / Brand" value={form.make} onChange={e=>s('make',e.target.value)} placeholder="e.g. Makino"/>
                <Inp label="Model" value={form.model} onChange={e=>s('model',e.target.value)}/>
               
                <div style={{ gridColumn:'1/-1' }}>
                  <label style={LS}>Description</label>
                  <textarea className="ap-inp" value={form.description} onChange={e=>s('description',e.target.value)}
                    rows={3} placeholder="Additional details about this asset…"
                    style={{ ...IS, resize:'vertical', fontFamily:'inherit' }}/>
                </div>
              </div>
              <SectionHeader>Status</SectionHeader>
              <div style={G2}>
                <Sel label="Plant Status" value={form.plant_status} onChange={e=>s('plant_status',e.target.value)}
                  options={PLANT_STATUS_OPTS}/>
                <Sel label="Finance Status" value={form.finance_status} onChange={e=>s('finance_status',e.target.value)}
                  options={[{value:'Active',label:'Active'},{value:'Retired',label:'Retired'}]}/>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:12 }}>
                <input type="checkbox" id="iscrit" checked={!!form.is_critical}
                  onChange={e=>s('is_critical',e.target.checked)} style={{ width:15,height:15,cursor:'pointer' }}/>
                <label htmlFor="iscrit" style={{ fontSize:13,color:'#374151',cursor:'pointer' }}>
                  ⚡ Mark as Critical Asset (triggers priority alerts)
                </label>
              </div>
            </div>
            {/* RIGHT — Image upload + Finance preview */}
            <div>
              <SectionHeader>Asset Image</SectionHeader>
              <div style={{ background:'#f8fafc', borderRadius:10, padding:14,
                border:'1px solid #e2e8f0', marginBottom:18 }}>
                {(localPreview || imagePreview) && (
                  <div style={{ marginBottom:10, position:'relative' }}>
                    <img src={localPreview || imagePreview} alt="Asset"
                      style={{ width:'100%', maxHeight:160, objectFit:'cover',
                        borderRadius:8, border:'1px solid #e2e8f0', display:'block' }}/>
                    {localPreview && (
                      <span style={{ position:'absolute', top:-5, right:-5,
                        background:'#16a34a', color:'#fff', borderRadius:'50%',
                        width:18, height:18, fontSize:10, display:'flex',
                        alignItems:'center', justifyContent:'center', fontWeight:700 }}>✓</span>
                    )}
                  </div>
                )}
                <button onClick={()=>imgRef.current?.click()}
                  style={{ width:'100%', padding:'8px 14px', background:'#fff',
                    border:'1.5px dashed #cbd5e1', borderRadius:7, cursor:'pointer',
                    fontSize:13, color:'#64748b' }}>
                  {(localPreview || imagePreview) ? '🔄 Change Image' : '📷 Upload Asset Image'}
                </button>
                <input ref={imgRef} type="file" accept="image/*" style={{ display:'none' }}
                  onChange={e=>{ const f=e.target.files[0]; if(f){ setImageFile(f); setLocalPreview(URL.createObjectURL(f)); } }}/>
                {(localPreview || imagePreview) && (
                  <button onClick={()=>{ setImageFile(null); setLocalPreview(null); setImagePreview(null); }}
                    style={{ marginTop:6, width:'100%', padding:'5px', background:'none',
                      border:'1px solid #fee2e2', borderRadius:6, cursor:'pointer',
                      fontSize:11, color:'#dc2626' }}>
                    ✕ Remove Image
                  </button>
                )}
              </div>
              {showDepreciationSection && (
              <>
              <SectionHeader>Quick Finance Summary</SectionHeader>
              <FinPreviewCard form={form} fin={fin} finValues={finValues} isNew={isNew} selDepType={selDepType} depMethods={depMethods}/>
              <SectionHeader>Depreciation Settings</SectionHeader>
              <div style={G2}>
                <Sel label="Depreciation Type" col="1/-1" value={form.dep_type_id}
                  onChange={e=>{ s('dep_type_id',e.target.value); autoFillSalvage(e.target.value); }}
                  options={depTypes.map(dt=>({value:dt.id,label:`${dt.code} — ${dt.description} (${dt.method_code||''})`}))}
                  placeholder="— Select Dep. Type —"/>
                {selDepType && (
                  <div style={{ gridColumn:'1/-1', background:'#f0f6ff', borderRadius:8,
                    padding:'10px 14px', fontSize:12, color:'#1a56db', lineHeight:1.6 }}>
                    <strong>Method:</strong> {selMethodCode}&nbsp;·&nbsp;
                    <strong>Calculation:</strong> {selDepType.calculation_method||'—'}&nbsp;·&nbsp;
                    <strong>Base:</strong> {selDepType.calculation_base==='M'?'Monthly':'Yearly'}
                    {selDepType.salvage_value_pct && <>&nbsp;·&nbsp;<strong>Salvage:</strong> {selDepType.salvage_value_pct}%</>}
                  </div>
                )}
                <NumIn label="Useful Life (Years)" value={form.useful_life_years} onChange={e=>s('useful_life_years',e.target.value)} placeholder="10"/>
                <div>
                  <label style={{ fontSize:12, fontWeight:600, color:'#475569', display:'block', marginBottom:4 }}>
                    Residual / Salvage Value (₹)
                    {selDepType?.salvage_value_pct > 0 && (
                      <span style={{ fontSize:10, color:'#94a3b8', fontWeight:400, marginLeft:6 }}>
                        auto-calc from {selDepType.salvage_value_pct}% salvage · editable
                      </span>
                    )}
                  </label>
                  <input type="number" value={form.residual_value||''} placeholder="0"
                    onChange={e=>s('residual_value',e.target.value)}
                    style={{ width:'100%', border:'1.5px solid #e2e8f0', borderRadius:7,
                      padding:'8px 11px', fontSize:13, outline:'none', boxSizing:'border-box',
                      background: selDepType?.salvage_value_pct > 0 ? '#f0f6ff' : '#fff',
                      color:'#1a1f2e' }}/>
                </div>
              </div>
  </>
)}
            </div>
          </div>
        )}

        {tab==='overview' && !editMode && profile && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>
            <div>
              <SectionHeader>Asset Details</SectionHeader>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <tbody>
                  <InfoRow label="Asset Code"     value={profile.asset_code} mono accent/>
                  <InfoRow label="Serial Number"  value={profile.serial_number} mono/>
                  <InfoRow label="Make / Model"   value={[profile.make,profile.model].filter(Boolean).join(' ')}/>
                  <InfoRow label="Category"       value={profile.category_name}/>
                  <InfoRow label="Location"       value={profile.location_name}/>
                  <InfoRow label="Department"     value={profile.department}/>
                  <InfoRow label="Assigned To"    value={profile.assigned_user_name}/>
                  <InfoRow label="Purchase Date"  value={fdt(profile.purchase_date)}/>
                  <InfoRow label="Purchase Cost"  value={fmt(profile.purchase_cost)}/>
                  <InfoRow label="Warranty Period"  value={wpName || '—'}/>
                  <InfoRow label="Warranty Start"   value={wStart ? fdt(wStart) : '—'}/>
                  <InfoRow label="Warranty Expiry"  value={wEnd ? fdt(wEnd) : '—'}/>
                  <InfoRow label="Warranty Status"  value={
                    wActive === true  ? <span style={{color:'#16a34a',fontWeight:700}}>✅ Active</span>  :
                    wActive === false ? <span style={{color:'#dc2626',fontWeight:700}}>❌ Expired</span> : null
                  }/>
                  <InfoRow label="Plant Status"   value={profile.plant_status}/>
                  <InfoRow label="Finance Status" value={profile.finance_status}/>
                  <InfoRow label="Status"         value={<Chip bg={sc.bg} c={sc.c}>{(profile.status||'').toUpperCase()}</Chip>}/>
                  {profile.is_critical && <InfoRow label="Critical" value="⚡ YES — Priority alerts active"/>}
                </tbody>
              </table>
              {profile.description && (
                <div style={{ marginTop:12, padding:12, background:'#f8fafc', borderRadius:8,
                  fontSize:13, color:'#64748b', lineHeight:1.6 }}>{profile.description}</div>
              )}
            </div>
            <div>
              <SectionHeader>Lifecycle Stage</SectionHeader>
              <LifecycleBar asset={profile}/>
              <SectionHeader>Recent Work Orders</SectionHeader>
              {wos.slice(0,5).length===0
                ? <div style={{ fontSize:13,color:'#94a3b8' }}>No work orders yet</div>
                : wos.slice(0,5).map(wo=>(
                  <div key={wo.id} style={{ display:'flex',alignItems:'center',gap:10,padding:'8px 0',
                    borderBottom:'1px solid #f1f5f9' }}>
                    <WOTypeBadge type={wo.type}/>
                    <div style={{ flex:1,fontSize:13,fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>
                      {wo.title||wo.wo_number}
                    </div>
                    <span style={{ fontSize:11,color:'#94a3b8' }}>{wo.created_at?.slice?.(0,10)}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* ╔══ PURCHASE & SUPPLIER ════════════════════════════╗ */}
        {tab==='purchase' && editMode && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:28 }}>
            <div>
              <SectionHeader>Invoice & Order Details</SectionHeader>
              <div style={G2}>
                <Inp label="Purchase Order #" value={form.po_number} onChange={e=>s('po_number',e.target.value)} placeholder="PO-2024-001"/>
                <DateIn label="Purchase Date" value={form.purchase_date} onChange={e=>s('purchase_date',e.target.value)}/>
                <Inp label="Our Invoice No" value={form.our_invoice_no} onChange={e=>s('our_invoice_no',e.target.value)} mono/>
                <DateIn label="Our Invoice Date" value={form.our_invoice_date} onChange={e=>s('our_invoice_date',e.target.value)}/>
                <Inp label="Supplier Invoice No" value={form.supplier_invoice_no} onChange={e=>s('supplier_invoice_no',e.target.value)} mono/>
                <DateIn label="Supplier Invoice Date" value={form.supplier_invoice_date} onChange={e=>s('supplier_invoice_date',e.target.value)}/>
                <DateIn label="Delivery Date" value={form.delivery_date} onChange={e=>s('delivery_date',e.target.value)}/>
                <DateIn label="Installation Date" value={form.installation_date} onChange={e=>s('installation_date',e.target.value)}/>
                <DateIn label="Commissioning Date" value={form.commission_date} onChange={e=>s('commission_date',e.target.value)}/>
                <DateIn label="Capitalised Date" value={form.capitalized_date} onChange={e=>s('capitalized_date',e.target.value)}/>
                <Sel label="Warranty Period" value={form.warranty_period_id} onChange={e=>s('warranty_period_id',e.target.value)}
                  options={warrantyPeriods.map(wp=>({value:String(wp.id),label:`${wp.code} — ${wp.warranty_name}`}))}
                  placeholder="— Select Warranty Period —"/>
              </div>
              <SectionHeader>Cost Details</SectionHeader>
              <div style={G2}>
                <NumIn label="Purchase / Acquisition Cost (₹)" col="1/-1"
                  value={form.purchase_cost}
                  onChange={e => {
                    const cost = e.target.value;
                    s('purchase_cost', cost);
                    const pct = parseFloat(form.gst_percent);
                    if (pct > 0) s('gst_amount', ((parseFloat(cost)||0) * pct / 100).toFixed(2));
                  }}/>
                <NumIn label="GST %" value={form.gst_percent} placeholder="18"
                  onChange={e => {
                    const pct = e.target.value;
                    s('gst_percent', pct);
                    const cost = parseFloat(form.purchase_cost);
                    if (cost > 0) s('gst_amount', (cost * (parseFloat(pct)||0) / 100).toFixed(2));
                  }}/>
                <div>
                  <label style={{ fontSize:12, fontWeight:600, color:'#475569', display:'block', marginBottom:4 }}>
                    GST Amount (₹)
                    <span style={{ fontSize:10, color:'#94a3b8', fontWeight:400, marginLeft:6 }}>
                      auto-calculated · editable
                    </span>
                  </label>
                  <input type="number" value={form.gst_amount||''}
                    onChange={e => s('gst_amount', e.target.value)}
                    style={{ width:'100%', border:'1.5px solid #e2e8f0', borderRadius:7,
                      padding:'8px 11px', fontSize:13, outline:'none',
                      boxSizing:'border-box', background:'#f0f6ff', color:'#1a1f2e' }}/>
                </div>
                {/* Installation Cost + Head YN */}
                <NumIn label="Installation Cost (₹)" value={form.installation_cost} onChange={e=>s('installation_cost',e.target.value)}/>
                <div>
                  <label style={{...LS}}>Installation – Cost Head YN</label>
                  <select className="ap-inp" style={{...IS}} value={form.installation_cost_head_yn||'N'} onChange={e=>s('installation_cost_head_yn',e.target.value)}>
                    <option value="N">N — Exclude from Cost Head</option>
                    <option value="Y">Y — Include in Cost Head</option>
                  </select>
                </div>
                {/* Transport Cost + Head YN */}
                <NumIn label="Transport Cost (₹)" value={form.transport_cost} onChange={e=>s('transport_cost',e.target.value)}/>
                <div>
                  <label style={{...LS}}>Transport – Cost Head YN</label>
                  <select className="ap-inp" style={{...IS}} value={form.transport_cost_head_yn||'N'} onChange={e=>s('transport_cost_head_yn',e.target.value)}>
                    <option value="N">N — Exclude from Cost Head</option>
                    <option value="Y">Y — Include in Cost Head</option>
                  </select>
                </div>
              </div>

              {/* ── SUBSEQUENT COSTS (multi-row) ───────── */}
              <div style={{ marginTop:20 }}>
                <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8 }}>
                  <span style={{ fontSize:11,fontWeight:700,color:'rgba(26,31,46,.4)',textTransform:'uppercase',letterSpacing:'.07em' }}>Subsequent Costs</span>
                  <button type="button"
                    onClick={()=>setSubsequentCosts(sc=>[...sc,{transport_cost:'',installation_cost:'',description:'',cost_date:'',include_in_cost_head:'N'}])}
                    style={{ padding:'5px 12px',background:'#1a56db',color:'#fff',border:'none',borderRadius:6,fontSize:12,fontWeight:600,cursor:'pointer' }}>
                    + Add Row
                  </button>
                </div>
                {subsequentCosts.length===0 ? (
                  <div style={{ padding:'10px',color:'#94a3b8',fontSize:13,textAlign:'center',border:'1px dashed #e2e8f0',borderRadius:8 }}>
                    No subsequent costs — click "+ Add Row"
                  </div>
                ) : subsequentCosts.map((sc,i)=>{
                  const upd=(k,v)=>setSubsequentCosts(list=>list.map((x,j)=>j===i?{...x,[k]:v}:x));
                  return (
                    <div key={i} style={{ background:'#f8fafc',borderRadius:10,border:'1px solid #e2e8f0',padding:'12px 14px',marginBottom:8 }}>
                      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8 }}>
                        <span style={{ fontSize:12,fontWeight:700,color:'#64748b' }}>Row {i+1}</span>
                        <button type="button" onClick={()=>setSubsequentCosts(list=>list.filter((_,j)=>j!==i))}
                          style={{ background:'none',border:'1px solid #fca5a5',borderRadius:5,cursor:'pointer',color:'#dc2626',fontSize:11,padding:'2px 8px' }}>✕ Remove</button>
                      </div>
                      <div style={G2}>
                        <div><label style={LS}>Transport Cost (₹)</label>
                          <input type="number" className="ap-inp" style={{...IS}} value={sc.transport_cost||''} onChange={e=>upd('transport_cost',e.target.value)} placeholder="0"/></div>
                        <div><label style={LS}>Installation Cost (₹)</label>
                          <input type="number" className="ap-inp" style={{...IS}} value={sc.installation_cost||''} onChange={e=>upd('installation_cost',e.target.value)} placeholder="0"/></div>
                        <div style={{ gridColumn:'1/-1' }}><label style={LS}>Description</label>
                          <input className="ap-inp" style={{...IS}} value={sc.description||''} onChange={e=>upd('description',e.target.value)} placeholder="e.g. Additional installation"/></div>
                        <div><label style={LS}>Cost Date</label>
                          <input type="date" className="ap-inp" style={{...IS}} value={sc.cost_date||''} onChange={e=>upd('cost_date',e.target.value)}/></div>
                        <div><label style={LS}>Cost Head YN</label>
                          <select className="ap-inp" style={{...IS}} value={sc.include_in_cost_head||'N'} onChange={e=>upd('include_in_cost_head',e.target.value)}>
                            <option value="N">N — Exclude from Cost Head</option>
                            <option value="Y">Y — Include in Cost Head</option>
                          </select></div>
                      </div>
                    </div>
                  );
                })}
                {/* Running total */}
                {(()=>{
                  const b=parseFloat(form.purchase_cost)||0;
                  const i2=form.installation_cost_head_yn==='Y'?(parseFloat(form.installation_cost)||0):0;
                  const t=form.transport_cost_head_yn==='Y'?(parseFloat(form.transport_cost)||0):0;
                  const st=subsequentCosts.filter(sc=>sc.include_in_cost_head==='Y').reduce((s,sc)=>s+(parseFloat(sc.transport_cost)||0)+(parseFloat(sc.installation_cost)||0),0);
                  const tot=b+i2+t+st;
                  return tot>0?(
                    <div style={{ marginTop:8,background:'#f0f6ff',borderRadius:8,padding:'8px 14px',border:'1px solid #dbeafe',fontSize:13,display:'flex',justifyContent:'space-between',alignItems:'center' }}>
                      <span style={{ color:'#64748b' }}>Cost Head Total:</span>
                      <strong style={{ color:'#1a56db',fontFamily:'monospace' }}>₹{tot.toLocaleString('en-IN',{minimumFractionDigits:2})}</strong>
                    </div>
                  ):null;
                })()}
              </div>

              <SectionHeader>Payment & Approval</SectionHeader>
              <div style={G2}>
                <Inp label="Payment Terms" value={form.payment_terms} onChange={e=>s('payment_terms',e.target.value)} placeholder="Net 30"/>
                <Sel label="Payment Status" value={form.payment_status} onChange={e=>s('payment_status',e.target.value)}
                  options={PAYMENT_STATUS_OPTS}/>
                <Inp label="CapEx Code" value={form.capex_code} onChange={e=>s('capex_code',e.target.value)} mono/>
                <Inp label="Approved By" value={form.approved_by} onChange={e=>s('approved_by',e.target.value)}/>
              </div>
            </div>
            <div>
              <SectionHeader>Supplier / Vendor</SectionHeader>
              <Sel label="Select Vendor" value={form.vendor_id} onChange={e=>s('vendor_id',e.target.value)}
                options={vendors.map(v=>({value:v.id,label:`${v.name} (${v.vendor_code})`}))} placeholder="— Select Vendor —"/>
              {form.vendor_id && (() => {
                const v = vendors.find(vv=>vv.id===parseInt(form.vendor_id));
                return v ? (
                  <div style={{ marginTop:12,background:'#f0f6ff',borderRadius:10,padding:'12px 14px',border:'1px solid #dbeafe' }}>
                    <div style={{ fontWeight:700,color:'#1a56db',fontSize:14 }}>🏭 {v.name}</div>
                    <div style={{ fontSize:12,color:'#64748b',marginTop:2 }}>{v.vendor_code}{v.email&&` · ${v.email}`}</div>
                  </div>
                ) : null;
              })()}
              {/* Purchase cost summary preview — use form so YN flags reflect current selections */}
              <div style={{ marginTop:28 }}>
                <SectionHeader>Cost Preview</SectionHeader>
                <div style={{ background:'#f8fafc',borderRadius:10,padding:'4px 14px 10px',border:'1px solid #e2e8f0' }}>
                  <CostTable fin={fin} profile={form} subsequentCosts={subsequentCosts}/>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab==='purchase' && !editMode && profile && (() => {
          /* Resolve warranty period name: API text field or master lookup */
          return (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>
            <div>
              <SectionHeader>Purchase Details</SectionHeader>
              <div style={{ background:'#f8fafc',borderRadius:10,padding:'4px 14px 10px',marginBottom:14 }}>
                <table style={{ width:'100%',borderCollapse:'collapse' }}>
                  <tbody>
                    <InfoRow label="PO Number"             value={profile.po_number} mono/>
                    <InfoRow label="Purchase Date"         value={fdt(profile.purchase_date)}/>
                    <InfoRow label="Our Invoice No"        value={profile.our_invoice_no} mono/>
                    <InfoRow label="Our Invoice Date"      value={fdt(profile.our_invoice_date)}/>
                    <InfoRow label="Supplier Invoice No"   value={profile.supplier_invoice_no} mono/>
                    <InfoRow label="Supplier Invoice Date" value={fdt(profile.supplier_invoice_date)}/>
                    <InfoRow label="Delivery Date"         value={fdt(profile.delivery_date)}/>
                    <InfoRow label="Installation Date"     value={fdt(profile.installation_date)}/>
                    <InfoRow label="Commissioning Date"    value={fdt(profile.commission_date)}/>
                    <InfoRow label="Capitalised Date"      value={fdt(profile.capitalized_date)}/>
                  </tbody>
                </table>
              </div>
              <SectionHeader>Cost Summary</SectionHeader>
              <div style={{ background:'#f8fafc',borderRadius:10,padding:'4px 14px 10px',border:'1px solid #e2e8f0' }}>
                <CostTable fin={fin} profile={profile} subsequentCosts={subsequentCosts}/>
              </div>
            </div>
            <div>
              <SectionHeader>Vendor</SectionHeader>
              {/* Subsequent Costs - view mode */}
              {subsequentCosts.length > 0 && (
                <div style={{ marginTop:16 }}>
                  <SectionHeader>Subsequent Costs</SectionHeader>
                  <div style={{ border:'1px solid #e2e8f0',borderRadius:10,overflow:'hidden' }}>
                    <table style={{ width:'100%',borderCollapse:'collapse' }}>
                      <thead><tr style={{ background:'#f8fafc' }}>
                        {['Transport','Installation','Description','Date','Cost Head'].map(h=>(
                          <th key={h} style={{ padding:'9px 12px',fontSize:11,fontWeight:700,textTransform:'uppercase',
                            letterSpacing:'.05em',color:'#94a3b8',borderBottom:'1px solid #e2e8f0',textAlign:'left' }}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {subsequentCosts.map((sc,i)=>(
                          <tr key={i} style={{ borderBottom:'1px solid #f1f5f9' }}>
                            <td style={{ padding:'8px 12px',fontSize:13,fontFamily:'monospace' }}>{sc.transport_cost?`₹${Number(sc.transport_cost).toLocaleString('en-IN')}`:'—'}</td>
                            <td style={{ padding:'8px 12px',fontSize:13,fontFamily:'monospace' }}>{sc.installation_cost?`₹${Number(sc.installation_cost).toLocaleString('en-IN')}`:'—'}</td>
                            <td style={{ padding:'8px 12px',fontSize:13,color:'#64748b' }}>{sc.description||'—'}</td>
                            <td style={{ padding:'8px 12px',fontSize:12,color:'#94a3b8' }}>{sc.cost_date?.slice?.(0,10)||'—'}</td>
                            <td style={{ padding:'8px 12px' }}>
                              <span style={{ padding:'2px 8px',borderRadius:20,fontSize:11,fontWeight:700,
                                background:sc.include_in_cost_head==='Y'?'#dcfce7':'#f1f5f9',
                                color:sc.include_in_cost_head==='Y'?'#16a34a':'#94a3b8' }}>
                                {sc.include_in_cost_head==='Y'?'✅ Y — In Cost Head':'⛔ N — Excluded'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {profile.vendor ? (
                <div style={{ border:'1px solid #e2e8f0',borderRadius:10,overflow:'hidden' }}>
                  <div style={{ background:'#f0f6ff',padding:'12px 14px',borderBottom:'1px solid #e2e8f0',display:'flex',gap:10 }}>
                    <div style={{ width:40,height:40,background:'#fff',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,border:'1px solid #e2e8f0' }}>🏭</div>
                    <div>
                      <div style={{ fontWeight:700,color:'#1a1f2e',fontSize:14 }}>{profile.vendor.name}</div>
                      <div style={{ fontSize:11,color:'#64748b' }}>{profile.vendor.vendor_code}</div>
                    </div>
                  </div>
                  <div style={{ padding:'4px 14px 10px' }}>
                    <table style={{ width:'100%',borderCollapse:'collapse' }}>
                      <tbody>
                        <InfoRow label="GST"     value={profile.vendor.gst_no} mono/>
                        <InfoRow label="Contact" value={profile.vendor.contact_person}/>
                        <InfoRow label="Mobile"  value={profile.vendor.mobile}/>
                        <InfoRow label="Email"   value={profile.vendor.email}/>
                        <InfoRow label="Support" value={profile.vendor.support_helpline}/>
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div style={{ padding:28,textAlign:'center',color:'#94a3b8',fontSize:13,border:'2px dashed #e2e8f0',borderRadius:10 }}>
                  <div style={{ fontSize:32,marginBottom:8 }}>🏭</div>
                  No vendor linked.<br/>
                  <button onClick={()=>{setEditMode(true);setTab('purchase');}} style={{ marginTop:10,fontSize:12,color:'#1a56db',background:'none',border:'1px solid #1a56db',padding:'5px 12px',borderRadius:6,cursor:'pointer' }}>
                    Edit to Link Vendor
                  </button>
                </div>
              )}
              <div style={{ marginTop:20 }}>
                <SectionHeader>Other Details</SectionHeader>
                <table style={{ width:'100%',borderCollapse:'collapse' }}>
                  <tbody>
                    <InfoRow label="Payment Terms"   value={profile.payment_terms}/>
                    <InfoRow label="Payment Status"  value={profile.payment_status}/>
                    <InfoRow label="Warranty Period" value={profile.warranty_period}/>
                    <InfoRow label="Warranty Start"  value={fdt(profile.warranty_start_date)}/>
                    <InfoRow label="Warranty End"    value={fdt(profile.warranty_end_date)}/>
                    <InfoRow label="Insurance"       value={(profile.valid_insurance==='Y'||profile.valid_insurance===true) ? `✅ Valid (${fdt(profile.valid_insurance_from)} – ${fdt(profile.valid_insurance_to)})` : (profile.valid_insurance==='N'?'❌ Not Available':undefined)}/>
                    <InfoRow label="AMC"             value={profile.valid_amc==='Y'?`✅ ${fdt(profile.valid_amc_from)} – ${fdt(profile.valid_amc_to)}`:undefined}/>
                    <InfoRow label="Plant Status"    value={profile.plant_status}/>
                    <InfoRow label="Finance Status"  value={profile.finance_status}/>
                    <InfoRow label="CapEx Code"      value={profile.capex_code} mono/>
                    <InfoRow label="Approved By"     value={profile.approved_by}/>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          );
        })()}

        {/* ╔══ WARRANTY & INSURANCE (edit only) ═══════════════╗ */}
        {tab==='warranty' && editMode && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:28 }}>
            <div>
              <SectionHeader>Warranty</SectionHeader>
              <div style={G2}>
              <Sel label="Warranty Period" value={form.warranty_period_id} onChange={e=>s('warranty_period_id',e.target.value)}
                  options={warrantyPeriods.map(wp=>({value:String(wp.id),label:`${wp.code} — ${wp.warranty_name}`}))}
                  placeholder="— Select Warranty Period —"/>
                {/* <Inp label="Warranty Period" col="1/-1" value={form.warranty_period} onChange={e=>s('warranty_period',e.target.value)} placeholder="e.g. 12 Months"/> */}
               
              </div>
              <div style={G2}>
              <DateIn label="Warranty Start Date" value={form.warranty_start_date} onChange={e=>s('warranty_start_date',e.target.value)}/>
              <DateIn label="Warranty End Date"   value={form.warranty_end_date}   onChange={e=>s('warranty_end_date',e.target.value)}/>
                </div>
              <SectionHeader>Insurance</SectionHeader>
              <Sel label="Valid Insurance" value={form.valid_insurance} onChange={e=>s('valid_insurance',e.target.value)}
                options={[{value:'N',label:'N — Not Available'},{value:'Y',label:'Y — Valid Insurance Exists'}]}/>
              {form.valid_insurance==='Y' && (
                <div style={{ ...G2, marginTop:12 }}>
                  <DateIn label="Insurance From Date" value={form.valid_insurance_from} onChange={e=>s('valid_insurance_from',e.target.value)}/>
                  <DateIn label="Insurance To Date"   value={form.valid_insurance_to}   onChange={e=>s('valid_insurance_to',e.target.value)}/>
                </div>
              )}
            </div>
            <div>
              <SectionHeader>AMC / Service Contract</SectionHeader>
              <Sel label="Valid AMC" value={form.valid_amc} onChange={e=>s('valid_amc',e.target.value)}
                options={[{value:'N',label:'N — Not Available'},{value:'Y',label:'Y — Valid AMC Exists'}]}/>
              {form.valid_amc==='Y' && (
                <div style={{ ...G2, marginTop:12 }}>
                  <DateIn label="AMC From Date" value={form.valid_amc_from} onChange={e=>s('valid_amc_from',e.target.value)}/>
                  <DateIn label="AMC To Date"   value={form.valid_amc_to}   onChange={e=>s('valid_amc_to',e.target.value)}/>
                  <Inp label="AMC Contract #"   value={form.amc_contract}   onChange={e=>s('amc_contract',e.target.value)}/>
                  <NumIn label="AMC Annual Cost (₹)" value={form.amc_cost}  onChange={e=>s('amc_cost',e.target.value)}/>
                  <DateIn label="AMC Start Date" value={form.amc_start}     onChange={e=>s('amc_start',e.target.value)}/>
                  <DateIn label="AMC End Date"   value={form.amc_end}       onChange={e=>s('amc_end',e.target.value)}/>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ╔══ FINANCE TAB ═════════════════════════════════════╗ */}
        {tab==='finance' && editMode && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:28 }}>
            <div>
              <SectionHeader>Asset Class</SectionHeader>
              <div style={G2}>
                <Sel label="Asset Class" col="1/-1" value={form.asset_class_id}
                  onChange={e=>{
                    s('asset_class_id',e.target.value);
                    const ac=assetClasses.find(a=>a.id===parseInt(e.target.value));
                    if(ac){
                      if(ac.dep_type_id) {
                        s('dep_type_id',String(ac.dep_type_id));
                        autoFillSalvage(ac.dep_type_id);
                      }
                      if(ac.useful_life_months) s('useful_life_years',String(Math.round(ac.useful_life_months/12)));
                    }
                  }}
                  options={assetClasses.map(ac=>({value:String(ac.id),label:`${ac.code} — ${ac.description} (${ac.dep_area})`}))}
                  placeholder="— Select Asset Class —"/>
              </div>
              <SectionHeader>Depreciation Configuration</SectionHeader>
              <div style={G2}>
                <Sel label="Depreciation Type" col="1/-1" value={form.dep_type_id}
                  onChange={e=>{ s('dep_type_id',e.target.value); autoFillSalvage(e.target.value); }}
                  options={depTypes.map(dt=>({value:dt.id,label:`${dt.code} — ${dt.description} (${dt.method_code||''})`}))}
                  placeholder="— Select Depreciation Type —"/>
                {selDepType && (
                  <div style={{ gridColumn:'1/-1', background:'#f0f6ff', borderRadius:8,
                    padding:'10px 14px', fontSize:12, color:'#1a56db', lineHeight:1.6 }}>
                    <strong>Method:</strong> {selMethodCode}&nbsp;·&nbsp;
                    <strong>Calc. Method:</strong> {selDepType.calculation_method||'—'}&nbsp;·&nbsp;
                    <strong>Base:</strong> {selDepType.calculation_base==='M'?'Monthly':'Yearly'}
                    {selDepType.salvage_value_pct!=null && <>&nbsp;·&nbsp;<strong>Salvage:</strong> {selDepType.salvage_value_pct}%</>}
                    {selDepType.percentage!=null && <>&nbsp;·&nbsp;<strong>Rate:</strong> {selDepType.percentage}%</>}
                  </div>
                )}
                <NumIn label="Useful Life (Years)" value={form.useful_life_years} onChange={e=>s('useful_life_years',e.target.value)} placeholder="10"/>
                <div>
                  <label style={{ fontSize:12, fontWeight:600, color:'#475569', display:'block', marginBottom:4 }}>
                    Residual / Salvage Value (₹)
                    {selDepType?.salvage_value_pct > 0 && (
                      <span style={{ fontSize:10, color:'#94a3b8', fontWeight:400, marginLeft:6 }}>
                        auto-calc from {selDepType.salvage_value_pct}% · editable
                      </span>
                    )}
                  </label>
                  <input type="number" value={form.residual_value||''} placeholder="0"
                    onChange={e=>s('residual_value',e.target.value)}
                    style={{ width:'100%', border:'1.5px solid #e2e8f0', borderRadius:7,
                      padding:'8px 11px', fontSize:13, outline:'none', boxSizing:'border-box',
                      background: selDepType?.salvage_value_pct > 0 ? '#f0f6ff' : '#fff',
                      color:'#1a1f2e' }}/>
                </div>
              </div>
            </div>
            <div>
              <SectionHeader>Finance Preview</SectionHeader>
              <FinPreviewCard form={form} fin={fin} nowLabel={nowLabel} finValues={finValues} isNew={isNew} selDepType={selDepType} depMethods={depMethods}/>
            </div>
          </div>
        )}

        {tab==='finance' && !editMode && profile && (
          <FinanceViewTab profile={profile} fin={fin} nowLabel={nowLabel}
            assetClasses={assetClasses}
            depTypes={depTypes} depMethods={depMethods}
            financialYears={financialYears}
            finValues={finValues} setFinValues={setFinValues} selFY={selFY} setSelFY={setSelFY}
            fyForm={fyForm} setFYForm={setFYForm}
            savingFY={savingFY} setSavingFY={setSavingFY}
            assetId={id}
            subsequentCosts={subsequentCosts}/>
        )}

        {/* ╔══ FULL HISTORY ════════════════════════════════════╗ */}
        {tab==='history' && !editMode && profile && (() => {
          /* ── All values from calcFin (already computed as `fin`) ── */
          const purchCost = fin.cost;
          const totalCap  = fin.total;
          const lifeYrs   = fin.life;
          const lifeMos   = lifeYrs * 12;
          const annDep    = fin.annDep;
          const accDep    = fin.accDep;
          const nbv       = fin.bookVal;
          const depRate   = fin.depRate;
          const capFYSt   = fin.capFYStart;
          const now       = new Date();
          const curFYSt   = now.getMonth()>=3 ? now.getFullYear() : now.getFullYear()-1;
          const curFY     = `${curFYSt}-${String(curFYSt+1).slice(-2)}`;
          const yearsUsed = fin.yu;
          const lifePct   = lifeMos>0 ? Math.round((yearsUsed*12/lifeMos)*100) : 0;

          /* Dep method name */
          const selDT   = depTypes?.find(dt=>dt.id===parseInt(profile.dep_type_id));
          const methNm  = selDT
            ? (depMethods?.find(m=>m.id===parseInt(selDT.method_id))?.description
               || selDT.method_desc || selDT.method_code
               || profile.depreciation_method?.toUpperCase() || 'SLM')
            : (profile.depreciation_method?.toUpperCase() || 'SLM');

          /* Maintenance stats */
          const brkWOs  = wos.filter(w=>w.type==='breakdown');
          const pmWOs   = wos.filter(w=>w.type==='pm');
          const maintCost = wos.reduce((s,w)=>s+(parseFloat(w.actual_cost||w.estimated_cost)||0),0);
          const activeWO  = wos.find(w=>['open','in_progress','assigned'].includes(w.status?.toLowerCase()));
          const lastBrk   = [...brkWOs].sort((a,b)=>new Date(b.created_at)-new Date(a.created_at))[0];

          /* Health score */
          const st = profile.current_status||profile.status||'operational';
          let health = 100;
          if (st.toLowerCase().includes('breakdown'))  health -= 20;
          if (profile.warranty_expired)                health -= 10;
          health -= Math.min(20, lifePct*0.2);
          if (brkWOs.length>3) health -= 10;
          health = Math.max(0, Math.round(health));
          const cond   = health>=70 ? 'Good' : health>=50 ? 'Attention Required' : 'Critical';
          const condC  = health>=70 ? '#16a34a' : health>=50 ? '#f59e0b' : '#dc2626';
          const condBg = health>=70 ? '#f0fdf4' : health>=50 ? '#fef3c7' : '#fff5f5';

          /* Timeline events */
          const fdate = s => s?new Date(s).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}):'—';
          const timeline = [
            purchCost  && { date:profile.purchase_date,     label:`Asset Purchased${profile.po_number?` (PO: ${profile.po_number})`:``}`, dot:'#16a34a' },
            profile.installation_date && { date:profile.installation_date, label:`Installed at ${profile.location_name||'Location'}`, dot:'#1a56db' },
            profile.capitalized_date  && { date:profile.capitalized_date,  label:'Capitalised', dot:'#6366f1' },
            profile.warranty_start_date    && { date:profile.warranty_start_date,    label:`Warranty Started (Till ${fdate(profile.warranty_end_date||profile.warranty_expiry)})`, dot:'#0d9488' },
            ( profile.warranty_end_date||profile.warranty_expiry) && new Date(profile.warranty_end_date||profile.warranty_expiry)<now && {
              date:(profile.warranty_end_date||profile.warranty_expiry), label:'Warranty Expired', dot:'#f59e0b', hl:true },
            ...brkWOs.slice(-3).map(w=>({
              date:w.created_at,
              label:`Breakdown WO Raised (${w.wo_number||`WO-${w.id}`})`,
              dot:'#dc2626', hl:true,
            })),
            { date:null, label:`Active Asset — ${st.charAt(0).toUpperCase()+st.slice(1)}`, dot:'#16a34a', cur:true },
          ].filter(Boolean).sort((a,b)=>{
            if(!a.date)return 1; if(!b.date)return -1;
            return new Date(a.date)-new Date(b.date);
          });

          /* Cost donut data */
          const donutSegs = [
            { label:'Purchase Cost',       value:purchCost,  color:'#1a56db' },
            { label:'Maintenance Cost',    value:maintCost,  color:'#10b981' },
            { label:'Depreciation Posted', value:accDep,     color:'#f59e0b' },
          ].filter(g=>g.value>0);
          const donutTotal = donutSegs.reduce((s,g)=>s+g.value,0)||1;
          let cumPct=0;
          const gradient = donutSegs.map(g=>{
            const pct=(g.value/donutTotal)*100;
            const from=cumPct.toFixed(1); cumPct+=pct;
            return `${g.color} ${from}% ${cumPct.toFixed(1)}%`;
          }).join(', ');

          /* Current FY fin values */
          const curFYFin = finValues.find(v=>v.financial_year===curFY)||{};

          /* Management remark */
          const remark = [
            `Asset ${profile.asset_code} (${profile.name}) was purchased on ${fdate(profile.purchase_date)} for ${fmt(Math.round(purchCost))}`,
            ` and currently has a Net Book Value of ${fmt(Math.round(nbv))}.`,
            profile.is_critical ? ' The asset is categorised as a critical asset' : ' The asset is a standard asset',
            st.toLowerCase().includes('breakdown') ? ' and is presently under Breakdown status.' : ' and is presently operational.',
            brkWOs.length>0 ? ` ${brkWOs.length} breakdown work order${brkWOs.length>1?'s have':' has'} been raised${maintCost>0?` with an estimated maintenance cost of ${fmt(Math.round(maintCost))}`:''}.` : '',
            profile.warranty_expiry
              ? (new Date(profile.warranty_expiry)<now ? ' Warranty has expired.' : ` Warranty is active until ${fdate(profile.warranty_expiry)}.`)
              : '',
            ` Asset has completed approximately ${lifePct}% of its useful life.`,
          ].join('');

          /* ── shared styles ── */
          const SEC_TITLE = {
            fontSize:11, fontWeight:800, color:'#1a56db', textTransform:'uppercase',
            letterSpacing:'.08em', marginBottom:16,
            paddingBottom:8, borderBottom:'2px solid #f1f5f9',
            display:'flex', alignItems:'center', justifyContent:'space-between',
          };
          const CARD = {
            background:'#fff', borderRadius:14, padding:'20px 22px',
            border:'1px solid #e2e8f0', boxShadow:'0 1px 4px rgba(0,0,0,.05)',
          };
          const fmtA = (n,d=0) => n!=null ? `₹${Number(n).toLocaleString('en-IN',{minimumFractionDigits:d,maximumFractionDigits:d})}` : '—';

          return (
            <div style={{ display:'flex', flexDirection:'column', gap:20, paddingBottom:24 }}>

              {/* ══ TOP KPI BANNER ═══════════════════════════════════════════════ */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
                {[
                  { icon:'₹',  label:'Total Capitalised Value', sub:'Acquisition Cost',      val:fmtA(Math.round(totalCap)),  color:'#1a1f2e', bg:'#f0f6ff' },
                  { icon:'📊', label:'Current NBV',             sub:`As on ${new Date().toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}`, val:fmtA(Math.round(nbv)), color:'#1a56db', bg:'#eff6ff' },
                  { icon:'📉', label:'Accumulated Depreciation',sub:'Total Posted',          val:fmtA(Math.round(accDep)),    color:'#dc2626', bg:'#fff5f5' },
                  { icon:'⏰', label:'Useful Life',             sub:`${lifeYrs} Yrs / ${lifeMos} Months`, val:`${lifeMos} Months`, color:'#1a1f2e', bg:'#f8fafc' },
                  { icon:'%',  label:'Depreciation Rate',       sub:'From Dep. Type Master',  val:`${depRate}% p.a.`,          color:'#1a1f2e', bg:'#f8fafc' },
                  { icon:'📋', label:'Depreciation Method',     sub:'Method',                 val:methNm,                      color:'#1a1f2e', bg:'#f8fafc' },
                ].map(k=>(
                  <div key={k.label} style={{ ...CARD, background:k.bg, padding:'16px 18px' }}>
                    <div style={{ fontSize:22, marginBottom:8 }}>{k.icon}</div>
                    <div style={{ fontSize:15, fontWeight:800, color:k.color, marginBottom:3 }}>{k.val}</div>
                    <div style={{ fontSize:11, fontWeight:700, color:'#374151' }}>{k.label}</div>
                    <div style={{ fontSize:10, color:'#94a3b8', marginTop:2 }}>{k.sub}</div>
                  </div>
                ))}
              </div>

              {/* ══ MAIN 2-COLUMN GRID ════════════════════════════════════════════ */}
              <div style={{ display:'grid', gridTemplateColumns:'1.1fr 0.9fr', gap:20, alignItems:'start' }}>

                {/* LEFT COLUMN */}
                <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

                  {/* ── Lifecycle Timeline ── */}
                  <div style={CARD}>
                    <div style={SEC_TITLE}>
                      <span>🕐 Lifecycle Timeline</span>
                      <span style={{ fontSize:10, color:'#94a3b8', fontWeight:400 }}>
                        {timeline.length - 1} events
                      </span>
                    </div>
                    <div style={{ display:'flex', flexDirection:'column' }}>
                      {timeline.map((ev,i)=>(
                        <div key={i} style={{ display:'flex', gap:16, paddingBottom:i<timeline.length-1?20:0 }}>
                          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', width:16, flexShrink:0 }}>
                            <div style={{ width:14, height:14, borderRadius:'50%', flexShrink:0, zIndex:1,
                              background: ev.cur ? ev.dot : (ev.hl ? ev.dot : '#fff'),
                              border:`2.5px solid ${ev.dot}`,
                              boxShadow: ev.cur ? `0 0 0 4px ${ev.dot}22` : 'none' }}/>
                            {i<timeline.length-1 && (
                              <div style={{ width:2, flex:1, background:'#e2e8f0', marginTop:4 }}/>
                            )}
                          </div>
                          <div style={{ paddingBottom:4, minWidth:0 }}>
                            <div style={{ fontSize:11, color:'#94a3b8', fontWeight:600, marginBottom:2 }}>
                              {ev.cur ? 'Current' : fdate(ev.date)}
                            </div>
                            <div style={{ fontSize:13, fontWeight:ev.hl||ev.cur?700:500,
                              color:ev.hl ? ev.dot : (ev.cur?'#1a1f2e':'#374151'), lineHeight:1.4 }}>
                              {ev.label}
                            </div>
                            {ev.cur && (
                              <span style={{ display:'inline-block', marginTop:5, padding:'2px 10px',
                                background:'#f0fdf4', color:'#16a34a', borderRadius:20,
                                fontSize:10, fontWeight:700, border:'1px solid #bbf7d0' }}>
                                • IN USE
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
 {/* ── Financial Summary ── */}
 <div style={CARD}>
                    <div style={SEC_TITLE}><span>📈 Financial Summary (FY {curFY})</span></div>
                    <div style={{ display:'flex', gap:16, alignItems:'center' }}>
                      <div style={{ flex:1 }}>
                        {[
                          ['Planned Depreciation', curFYFin.planned_depreciation
                            ? fmtA(curFYFin.planned_depreciation,2) : fmtA(annDep,2)],
                          ['Posted Depreciation',  curFYFin.posted_depreciation
                            ? fmtA(curFYFin.posted_depreciation,2)  : '— Not posted'],
                          ['YTD Depreciation %',   curFYFin.planned_depreciation && curFYFin.posted_depreciation
                            ? `${((curFYFin.posted_depreciation/curFYFin.planned_depreciation)*100).toFixed(2)}%` : '—'],
                        ].map(([l,v],i)=>(
                          <div key={l} style={{ display:'flex', justifyContent:'space-between',
                            padding:'7px 0', borderBottom:i<2?'1px solid #f8fafc':'none' }}>
                            <span style={{ fontSize:12, color:'#64748b' }}>{l}</span>
                            <span style={{ fontSize:12, fontWeight:600, color:'#1a1f2e', fontFamily:'monospace' }}>{v}</span>
                          </div>
                        ))}
                        <div style={{ display:'flex', justifyContent:'space-between',
                          alignItems:'center', paddingTop:10, marginTop:4 }}>
                          <span style={{ fontSize:14, fontWeight:800, color:'#1a56db' }}>Current NBV</span>
                          <span style={{ fontSize:16, fontWeight:800, color:'#1a56db', fontFamily:'monospace' }}>
                            {curFYFin.nbv_year_end ? fmtA(curFYFin.nbv_year_end,2) : fmtA(Math.round(nbv))}
                          </span>
                        </div>
                      </div>
                      {/* Life ring */}
                      {(() => {
                        const r=38, circ=2*Math.PI*r, filled=(lifePct/100)*circ;
                        return (
                          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, flexShrink:0 }}>
                            <div style={{ position:'relative', width:88, height:88 }}>
                              <svg viewBox="0 0 100 100" style={{ width:88, height:88 }}>
                                <circle cx="50" cy="50" r={r} fill="none" stroke="#e2e8f0" strokeWidth="9"/>
                                <circle cx="50" cy="50" r={r} fill="none" stroke="#1a56db" strokeWidth="9"
                                  strokeLinecap="round"
                                  strokeDasharray={`${filled} ${circ}`}
                                  transform="rotate(-90 50 50)"/>
                              </svg>
                              <div style={{ position:'absolute', inset:0, display:'flex',
                                flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
                                <span style={{ fontSize:14, fontWeight:800, color:'#1a56db', lineHeight:1 }}>{lifePct}%</span>
                              </div>
                            </div>
                            <div style={{ textAlign:'center' }}>
                              <div style={{ fontSize:10, color:'#64748b', lineHeight:1.3 }}>Useful Life</div>
                              <div style={{ fontSize:10, color:'#64748b', lineHeight:1.3 }}>Completed</div>
                              <div style={{ fontSize:9, color:'#94a3b8', marginTop:2 }}>
                                ({yearsUsed*12} / {lifeMos} Months)
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* ── Asset Health Indicators ── */}
                  <div style={CARD}>
                    <div style={SEC_TITLE}><span>🩺 Asset Health Indicators</span></div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                      {[
                        { label:'Availability',        val:'95%',       color:'#16a34a', bars:9 },
                        { label:'Insurance',           val:profile.valid_insurance?'Active':(profile.valid_insurance===false?'Inactive':'—'), color:profile.valid_insurance?'#16a34a':'#94a3b8' },
                        { label:'Breakdown Frequency', val:brkWOs.length<2?'Low':brkWOs.length<5?'Medium':'High',
                          color:brkWOs.length<2?'#10b981':brkWOs.length<5?'#f59e0b':'#dc2626', bars:null },
                        { label:'Criticality',         val:profile.is_critical?'High':'Normal', color:profile.is_critical?'#dc2626':'#16a34a' },
                        { label:'Warranty',            val:(profile.warranty_end_date||profile.warranty_expiry)?(new Date(profile.warranty_end_date||profile.warranty_expiry)<now?'Expired':'Active'):'—',
                          color:(profile.warranty_end_date||profile.warranty_expiry)?(new Date(profile.warranty_end_date||profile.warranty_expiry)<now?'#dc2626':'#16a34a'):'#94a3b8' },
                        { label:'Current Condition',   val:cond, color:condC },
                      ].map(ind=>(
                        <div key={ind.label} style={{ background:'#f8fafc', borderRadius:8,
                          padding:'10px 12px', border:'1px solid #f1f5f9' }}>
                          <div style={{ fontSize:10, color:'#94a3b8', marginBottom:6 }}>{ind.label}</div>
                          {ind.bars!=null && (
                            <div style={{ display:'flex', gap:2, marginBottom:6 }}>
                              {Array.from({length:10}).map((_,j)=>(
                                <div key={j} style={{ flex:1, height:4, borderRadius:2,
                                  background:j<ind.bars?ind.color:'#e2e8f0' }}/>
                              ))}
                            </div>
                          )}
                          <div style={{ fontSize:12, fontWeight:700, color:ind.color }}>{ind.val}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ── Cost Analysis ── */}
                  <div style={CARD}>
                    <div style={SEC_TITLE}><span>💰 Cost Analysis (Lifetime)</span></div>
                    <div style={{ display:'flex', gap:24, alignItems:'center', marginBottom:20 }}>
                      {/* CSS Donut */}
                      <div style={{ position:'relative', width:130, height:130, flexShrink:0 }}>
                        <div style={{ width:130, height:130, borderRadius:'50%',
                          background:`conic-gradient(${gradient})` }}/>
                        <div style={{ position:'absolute', inset:26, borderRadius:'50%',
                          background:'#fff', display:'flex', flexDirection:'column',
                          alignItems:'center', justifyContent:'center' }}>
                          <div style={{ fontSize:11, fontWeight:800, color:'#1a1f2e' }}>
                            {fmtA(Math.round(donutTotal))}
                          </div>
                          <div style={{ fontSize:9, color:'#94a3b8' }}>Total</div>
                        </div>
                      </div>
                      {/* Legend */}
                      <div style={{ flex:1 }}>
                        {donutSegs.map(g=>{
                          const pct=(g.value/donutTotal*100).toFixed(1);
                          return (
                            <div key={g.label} style={{ display:'flex', alignItems:'center',
                              justifyContent:'space-between', padding:'6px 0',
                              borderBottom:'1px solid #f8fafc' }}>
                              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                <div style={{ width:10, height:10, borderRadius:3, background:g.color, flexShrink:0 }}/>
                                <span style={{ fontSize:12, color:'#374151' }}>{g.label}</span>
                              </div>
                              <div style={{ textAlign:'right' }}>
                                <div style={{ fontSize:12, fontWeight:700, color:'#1a1f2e', fontFamily:'monospace' }}>
                                  {fmtA(Math.round(g.value))}
                                </div>
                                <div style={{ fontSize:10, color:'#94a3b8' }}>{pct}%</div>
                              </div>
                            </div>
                          );
                        })}
                        <div style={{ display:'flex', justifyContent:'space-between',
                          alignItems:'center', paddingTop:10, marginTop:4 }}>
                          <span style={{ fontSize:13, fontWeight:700, color:'#1a1f2e' }}>Total Cost (Till Date)</span>
                          <span style={{ fontSize:14, fontWeight:800, color:'#1a56db', fontFamily:'monospace' }}>
                            {fmtA(Math.round(donutTotal))}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── Management Remark ── */}
               {/*    <div style={{ ...CARD, background:'#fafbff', border:'1px dashed #c7d7f9' }}>
                    <div style={SEC_TITLE}><span>📝 Management Remark</span></div>
                    <p style={{ fontSize:13, color:'#374151', lineHeight:1.8, margin:0 }}>
                      {remark}
                    </p>
                  </div> */}
                </div>

                {/* RIGHT COLUMN */}
                <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

                  {/* ── Maintenance Summary ── */}
                  <div style={CARD}>
                    <div style={SEC_TITLE}><span>🔧 Maintenance Summary</span></div>
                    {/* 4 stats */}
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
                      {[
                        { label:'Breakdown Tickets', val:brkWOs.length,  icon:'🔴', color:'#dc2626' },
                        { label:'PM Tickets',         val:pmWOs.length,   icon:'🔧', color:'#1a56db' },
                        { label:'Total Work Orders',  val:wos.length,     icon:'📋', color:'#374151' },
                        { label:'Maintenance Cost',   val:fmtA(Math.round(maintCost)), icon:'₹', color:'#10b981' },
                      ].map(k=>(
                        <div key={k.label} style={{ background:'#f8fafc', borderRadius:10,
                          padding:'12px 14px', border:'1px solid #f1f5f9' }}>
                          <div style={{ fontSize:18, marginBottom:4 }}>{k.icon}</div>
                          <div style={{ fontSize:15, fontWeight:800, color:k.color }}>{k.val}</div>
                          <div style={{ fontSize:10, color:'#94a3b8', marginTop:2, lineHeight:1.3 }}>{k.label}</div>
                        </div>
                      ))}
                    </div>
                    {/* Rows */}
                    {[
                      ['Last Breakdown Date', lastBrk?fdate(lastBrk.created_at):'— No breakdowns',
                        lastBrk?'#dc2626':'#94a3b8'],
                    ].map(([l,v,c])=>(
                      <div key={l} style={{ display:'flex', justifyContent:'space-between',
                        padding:'8px 0', borderBottom:'1px solid #f8fafc' }}>
                        <span style={{ fontSize:13, color:'#64748b' }}>{l}</span>
                        <span style={{ fontSize:13, fontWeight:600, color:c }}>{v}</span>
                      </div>
                    ))}
                    <div style={{ display:'flex', justifyContent:'space-between',
                      padding:'8px 0', borderBottom:'1px solid #f8fafc', alignItems:'center' }}>
                      <span style={{ fontSize:13, color:'#64748b' }}>Current WO Status</span>
                      {activeWO ? (
                        <span style={{ fontSize:11, fontWeight:700, color:'#1a56db',
                          background:'#eff6ff', padding:'3px 10px', borderRadius:20,
                          border:'1px solid #bfdbfe' }}>
                          ● {activeWO.status?.toUpperCase().replace('_',' ')}
                        </span>
                      ) : (
                        <span style={{ fontSize:11, fontWeight:600, color:'#16a34a',
                          background:'#f0fdf4', padding:'3px 10px', borderRadius:20 }}>
                          ✓ No Active WO
                        </span>
                      )}
                    </div>
                    {/* Health score */}
                    <div style={{ padding:'10px 0' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                        <span style={{ fontSize:13, color:'#64748b' }}>Health Score</span>
                        <span style={{ fontSize:13, fontWeight:700, color:condC }}>{health} / 100</span>
                      </div>
                      <div style={{ height:8, background:'#f1f5f9', borderRadius:4, overflow:'hidden', marginBottom:10 }}>
                        <div style={{ height:'100%', width:`${health}%`, borderRadius:4,
                          background:condC, transition:'width .6s ease' }}/>
                      </div>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <span style={{ fontSize:13, color:'#64748b' }}>Current Condition</span>
                        <span style={{ fontSize:11, fontWeight:700, color:condC,
                          background:condBg, padding:'3px 12px', borderRadius:20 }}>
                          ● {cond.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* ── Vendor & Warranty ── */}
                  <div style={CARD}>
                    <div style={SEC_TITLE}><span>🤝 Vendor & Warranty</span></div>
                    {/* Vendor info */}
                    {profile.vendor ? (
                      <div style={{ display:'flex', gap:14, alignItems:'center',
                        paddingBottom:14, marginBottom:14, borderBottom:'1px solid #f1f5f9' }}>
                        <div style={{ width:48, height:48, borderRadius:12, background:'#f0f4ff',
                          display:'flex', alignItems:'center', justifyContent:'center',
                          fontSize:24, flexShrink:0 }}>🏢</div>
                        <div>
                          <div style={{ fontSize:14, fontWeight:700, color:'#1a1f2e' }}>
                            {profile.vendor.name || '—'}
                          </div>
                          <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>
                            {profile.vendor.vendor_code}
                            {profile.vendor.gst_no && ` · GST: ${profile.vendor.gst_no}`}
                          </div>
                          {profile.vendor.is_preferred && (
                            <span style={{ display:'inline-block', marginTop:4, fontSize:10,
                              fontWeight:700, color:'#16a34a', background:'#dcfce7',
                              padding:'2px 8px', borderRadius:20 }}>★ Preferred Vendor</span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div style={{ paddingBottom:14, marginBottom:14, borderBottom:'1px solid #f1f5f9',
                        fontSize:13, color:'#94a3b8' }}>No vendor linked</div>
                    )}
                    {/* Status rows */}
                    {(() => {
                      const wExpiry = profile.warranty_end_date;
                      const wActive = wExpiry ? new Date(wExpiry) >= now : false;
                      const insActive = profile.valid_insurance === 'Y' || profile.valid_insurance === true;
                      return [
                        ['Payment Status',  profile.payment_status||'—',
                          profile.payment_status?.toLowerCase().includes('paid')?'#16a34a':'#f59e0b'],
                        ['Warranty Period', profile.warranty_period||'—', '#374151'],
                        ['Warranty Expiry', wExpiry ? fdate(wExpiry) : '—',
                          wExpiry ? (wActive?'#16a34a':'#dc2626') : '#94a3b8'],
                        ['Warranty Status', wExpiry ? (wActive?'Active':'Expired') : '—',
                          wActive ? '#16a34a' : '#dc2626'],
                        ['Insurance',       insActive ? 'Active' : (insActive===false?'Inactive':'—'),
                          insActive ? '#16a34a' : '#dc2626'],
                        ...(profile.valid_insurance_from ? [
                          ['Insurance Valid', `${fdate(profile.valid_insurance_from)} – ${fdate(profile.valid_insurance_to)}`, '#374151']
                        ] : []),
                        ...(profile.valid_amc==='Y'||profile.amc_contract ? [
                          ['AMC Contract', profile.amc_contract||'—', '#374151'],
                          ['AMC Valid', profile.amc_start ? `${fdate(profile.amc_start)} – ${fdate(profile.amc_end)}` : '—', '#374151'],
                        ] : []),
                      ].map(([l,v,cl])=>(
                        <div key={l} style={{ display:'flex', justifyContent:'space-between',
                          padding:'8px 0', borderBottom:'1px solid #f8fafc' }}>
                          <span style={{ fontSize:13, color:'#64748b' }}>{l}</span>
                          <span style={{ fontSize:13, fontWeight:700, color:cl }}>{v}</span>
                        </div>
                      ));
                    })()}
                  </div>

                 
                </div>{/* end RIGHT COLUMN */}
              </div>{/* end 2-col grid */}
            </div>
          );
        })()}

        {/* ╔══ TICKETS / WOs ════════════════════════════════════╗ */}
        {tab==='tickets' && !editMode && (
          <div>
            <div style={{ display:'flex',justifyContent:'space-between',marginBottom:14 }}>
              <span style={{ fontSize:13,fontWeight:600,color:'#1a1f2e' }}>Work Orders — {profile?.asset_code}</span>
              <div style={{ display:'flex',gap:8 }}>
                <ActionBtn color="red"  onClick={()=>{setWoModal('breakdown');usersAPI.list().then(r=>setUsers(r.data?.data||r.data||[])).catch(()=>{}); mastersAPI.listFrequencies().then(r=>setFrequencies(r.data||[])).catch(()=>{}); mastersAPI.listMaintenanceTypes().then(r=>setMaintenanceTypes(r.data||[])).catch(()=>{}); mastersAPI.listFailureTypes().then(r=>setFailureTypes(r.data||[])).catch(()=>{})}}>⚡ Breakdown</ActionBtn>
                <ActionBtn color="teal" onClick={()=>{setWoModal('pm');usersAPI.list().then(r=>setUsers(r.data?.data||r.data||[])).catch(()=>{}); mastersAPI.listFrequencies().then(r=>setFrequencies(r.data||[])).catch(()=>{}); mastersAPI.listMaintenanceTypes().then(r=>setMaintenanceTypes(r.data||[])).catch(()=>{}); mastersAPI.listFailureTypes().then(r=>setFailureTypes(r.data||[])).catch(()=>{})}}>🔧 PM</ActionBtn>
              </div>
            </div>
            {wos.length===0 ? <EmptyState icon="🔧" text="No work orders yet"/> : (
              <div style={{ border:'1px solid #e2e8f0',borderRadius:10,overflow:'hidden' }}>
                <table style={{ width:'100%',borderCollapse:'collapse' }}>
                  <thead><tr style={{ background:'#f8fafc' }}>
                    {['WO#','Type','Title','Assigned','Date','Cost','Status'].map(h=>(
                      <th key={h} style={{ padding:'10px 14px',fontSize:11,fontWeight:700,textTransform:'uppercase',
                        letterSpacing:'.05em',color:'#94a3b8',borderBottom:'1px solid #e2e8f0',textAlign:'left',whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {wos.map(wo=>(
                      <tr key={wo.id} style={{ borderBottom:'1px solid #f1f5f9',cursor:'pointer' }} onClick={()=>navigate(`/work-orders/${wo.id}`)}>
                        <td style={{ padding:'10px 14px',fontFamily:'monospace',fontSize:12,color:'#1a56db' }}>{wo.wo_number}</td>
                        <td style={{ padding:'10px 14px' }}><WOTypeBadge type={wo.type}/></td>
                        <td style={{ padding:'10px 14px',fontSize:13,fontWeight:500,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{wo.title||'—'}</td>
                        <td style={{ padding:'10px 14px',fontSize:13,color:'#64748b' }}>{wo.assigned_name||'—'}</td>
                        <td style={{ padding:'10px 14px',fontSize:12,color:'#94a3b8' }}>{wo.created_at?.slice?.(0,10)}</td>
                        <td style={{ padding:'10px 14px',fontSize:13 }}>{wo.cost?fmt(wo.cost):'—'}</td>
                        <td style={{ padding:'10px 14px' }}><StatusChip status={wo.status}/></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ╔══ DOCUMENTS ═══════════════════════════════════════╗ */}
        {tab==='documents' && !editMode && (
          <div>
            <div style={{ background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:10,padding:16,marginBottom:20 }}>
              <div style={{ fontSize:12,fontWeight:600,color:'#64748b',marginBottom:10 }}>Upload Document</div>
              <div style={{ display:'flex',gap:10,flexWrap:'wrap',alignItems:'flex-end' }}>
                <div style={{ flex:2,minWidth:160 }}>
                  <label style={LS}>Title *</label>
                  <input value={docTitle} onChange={e=>setDocTitle(e.target.value)} placeholder="e.g. Operation Manual"
                    style={{ ...IS,width:'100%' }}/>
                </div>
                <div style={{ flex:1,minWidth:110 }}>
                  <label style={LS}>Type</label>
                  <select value={docType} onChange={e=>setDocType(e.target.value)} style={{ ...IS,width:'100%' }}>
                    {['manual','invoice','certificate','amc','warranty','photo','other'].map(t=>(
                      <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div style={{ flex:2,minWidth:140 }}>
                  <label style={LS}>File (optional)</label>
                  <input ref={fileRef} type="file" onChange={e=>setDocFile(e.target.files[0])}
                    style={{ padding:'7px 10px',background:'#107a5e',color:'#fff',border:'none',
                      borderRadius:6,fontSize:12,fontWeight:500,cursor:'pointer',height:38,display:'block',width:'100%' }}/>
                </div>
                <button onClick={uploadDoc} disabled={uploading} style={{ padding:'7px 18px',background:'#1a56db',
                  color:'#fff',border:'none',borderRadius:6,fontSize:13,fontWeight:500,cursor:'pointer',height:38,opacity:uploading?.6:1 }}>
                  {uploading?'Uploading…':'⬆ Upload'}
                </button>
              </div>
            </div>
            {docs.length===0 ? <EmptyState icon="📄" text="No documents attached yet"/> :
              docs.map(doc=>(
                <div key={doc.id} style={{ background:'#fff',border:'1px solid #e2e8f0',borderRadius:8,
                  padding:'12px 16px',display:'flex',alignItems:'center',gap:12,marginBottom:8 }}>
                  <div style={{ fontSize:28 }}>
                    {doc.doc_type==='invoice'?'🧾':doc.doc_type==='certificate'?'📋':doc.doc_type==='amc'?'📑':doc.doc_type==='photo'?'🖼':'📄'}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600,fontSize:13,color:'#1a1f2e' }}>{doc.title}</div>
                    <div style={{ fontSize:11,color:'#94a3b8',marginTop:2 }}>{doc.doc_type} · {doc.file_name||'No file'} · {fdt(doc.uploaded_at)}</div>
                  </div>
                  {doc.file_name && <a href={`/uploads/asset_docs/${doc.file_name}`} target="_blank" rel="noreferrer"
                    style={{ fontSize:11,padding:'4px 10px',border:'1px solid #e2e8f0',borderRadius:6,color:'#1a56db',textDecoration:'none',fontWeight:500 }}>View</a>}
                  <button onClick={()=>deleteDoc(doc.id)} style={{ fontSize:11,padding:'4px 10px',border:'1px solid #fee2e2',borderRadius:6,background:'#fff',color:'#dc2626',cursor:'pointer' }}>Delete</button>
                </div>
              ))
            }
          </div>
        )}

        {/* ╔══ SPARE PARTS ═════════════════════════════════════╗ */}
        {tab==='spareparts' && !editMode && (
          <div>
            <div style={{ fontSize:13,fontWeight:700,color:'#1a1f2e',marginBottom:12 }}>
              Linked Spare Parts — {profile?.asset_code}
            </div>
            {sprs.length===0 ? <div style={{ color:'#94a3b8',fontSize:13,marginBottom:16 }}>No spare parts linked yet.</div> : (
              <div style={{ border:'1px solid #e2e8f0',borderRadius:10,overflow:'hidden',marginBottom:20 }}>
                <table style={{ width:'100%',borderCollapse:'collapse' }}>
                  <thead><tr style={{ background:'#f8fafc' }}>
                    {['Part #','Name','Stock','Reorder','Cost','Status',''].map(h=>(
                      <th key={h} style={{ padding:'10px 14px',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',color:'#94a3b8',borderBottom:'1px solid #e2e8f0',textAlign:'left' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {sprs.map((sp,i)=>{
                      const low=sp.stock_qty<=sp.reorder_level,oos=sp.stock_qty===0;
                      return (
                        <tr key={sp.id} style={{ borderBottom:i<sprs.length-1?'1px solid #f1f5f9':'none' }}>
                          <td style={{ padding:'10px 14px',fontFamily:'monospace',fontSize:12,color:'#64748b' }}>{sp.part_number}</td>
                          <td style={{ padding:'10px 14px',fontSize:13,fontWeight:500 }}>{sp.name}</td>
                          <td style={{ padding:'10px 14px',fontSize:13,fontWeight:700,color:oos?'#dc2626':low?'#d97706':'#16a34a' }}>{sp.stock_qty} {sp.unit}</td>
                          <td style={{ padding:'10px 14px',fontSize:13,color:'#64748b' }}>{sp.reorder_level}</td>
                          <td style={{ padding:'10px 14px',fontSize:13 }}>{fmt(sp.unit_cost)}</td>
                          <td style={{ padding:'10px 14px' }}>
                            {oos?<Chip bg="#fee2e2" c="#dc2626">OUT</Chip>:low?<Chip bg="#fef3c7" c="#d97706">LOW</Chip>:<Chip bg="#dcfce7" c="#16a34a">OK</Chip>}
                          </td>
                          <td style={{ padding:'10px 14px' }}>
                            <button onClick={()=>unlinkSpare(sp.id)} style={{ fontSize:11,padding:'4px 10px',background:'#fff',border:'1px solid #fca5a5',borderRadius:6,cursor:'pointer',color:'#dc2626',fontWeight:500 }}>Remove</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ fontSize:11,fontWeight:700,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:8 }}>Link Spare Parts</div>
            <div style={{ position:'relative' }}>
              <input value={spareSearch}
                onChange={e=>{ setSpareSearch(e.target.value); setShowDrop(e.target.value.trim().length>=3); }}
                placeholder="Type 3+ characters to search parts…"
                style={{ ...IS,paddingRight:36 }}/>
              {spareSearch && <button onClick={()=>{ setSpareSearch(''); setShowDrop(false); }} style={{ position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#94a3b8',fontSize:16 }}>✕</button>}
              {showDrop && (
                <div style={{ position:'absolute',top:'calc(100% + 4px)',left:0,right:0,background:'#fff',border:'1px solid #e2e8f0',borderRadius:10,boxShadow:'0 8px 24px rgba(0,0,0,.1)',zIndex:99,padding:'4px 0' }}>
                  {filtSpares.length===0
                    ? <div style={{ padding:'16px 14px',fontSize:13,color:'#94a3b8',textAlign:'center' }}>No results for "{spareSearch}"</div>
                    : filtSpares.slice(0,8).map((sp,i)=>{
                      const low=sp.stock_qty<=sp.reorder_level,oos=sp.stock_qty===0;
                      return (
                        <div key={sp.id} style={{ display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderBottom:i<Math.min(filtSpares.length,8)-1?'1px solid #f1f5f9':'none' }}
                          onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
                          onMouseLeave={e=>e.currentTarget.style.background=''}>
                          <div style={{ width:32,height:32,background:'#f0f6ff',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,flexShrink:0 }}>⚙</div>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:13,fontWeight:600,color:'#1a1f2e' }}>{sp.name}</div>
                            <div style={{ fontSize:11,color:'#94a3b8' }}>{sp.part_number} · Stock: {sp.stock_qty} · {fmt(sp.unit_cost)}</div>
                          </div>
                          <Chip bg={oos?'#fee2e2':low?'#fef3c7':'#dcfce7'} c={oos?'#dc2626':low?'#d97706':'#16a34a'}>
                            {oos?'Out of Stock':low?'Low Stock':'In Stock'}
                          </Chip>
                          <button onClick={()=>linkSpare(sp.id)} disabled={linking} style={{ padding:'6px 14px',background:'#1a56db',color:'#fff',border:'none',borderRadius:7,fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap' }}>
                            {linking?'…':'+ Link'}
                          </button>
                        </div>
                      );
                    })
                  }
                  {filtSpares.length>0 && (
                    <div style={{ padding:'8px 14px',background:'#f8fafc',borderTop:'1px solid #f1f5f9',fontSize:11,color:'#94a3b8',display:'flex',justifyContent:'space-between' }}>
                      <span>{filtSpares.length} result{filtSpares.length!==1?'s':''}</span>
                      <span>Click + Link to attach</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            {spareSearch.trim().length>0 && spareSearch.trim().length<3 && (
              <div style={{ marginTop:6,fontSize:12,color:'#94a3b8' }}>
                <span style={{ color:'#1a56db',fontWeight:600 }}>{3-spareSearch.trim().length} more</span> character{3-spareSearch.trim().length!==1?'s':''} needed
              </div>
            )}
          </div>
        )}
      </div>{/* end body */}

      {/* ── MODALS ─────────────────────────────────────────── */}
      {qrOpen && profile && (
        <AssetQRModal asset={profile} onClose={()=>setQrOpen(false)}
          onSaved={r=>setProfile(p=>({...p,qr_code:r.qr_code,qr_code_url:r.qr_code_url}))}/>
      )}
      {woModal && profile && (
        <WOActionModal type={woModal} asset={profile} users={users}
          frequencies={frequencies}
          maintenanceTypes={maintenanceTypes}
          failureTypes={failureTypes}
          onClose={()=>setWoModal(null)}
          onCreated={()=>{ setWoModal(null); loadProfile(); toast.success('Work order created!'); }}/>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   EXTRACTED SUB-COMPONENTS
══════════════════════════════════════════════════════════════ */

const fmt2 = n => n!=null && n!=='' ? `₹${Number(n).toLocaleString('en-IN')}` : '—';

function CostTable({ fin, profile, subsequentCosts }) {
  const fmt2l = n => n!=null && n!=='' && n!==0 ? `₹${Number(n).toLocaleString('en-IN',{minimumFractionDigits:2})}` : '—';
  // Base rows — include install/transport only if YN = 'Y'
  const rows = [
    { label:'Acquisition & Production Costs', val:fin.cost, included:true },
    { label:'Installation Cost', val:fin.inst,
      included: profile?.installation_cost_head_yn==='Y',
      flag: profile?.installation_cost_head_yn==='Y' ? '✅ In Cost Head' : '⛔ Excluded' },
    { label:'Transport Cost', val:fin.trans,
      included: profile?.transport_cost_head_yn==='Y',
      flag: profile?.transport_cost_head_yn==='Y' ? '✅ In Cost Head' : '⛔ Excluded' },
  ];
  // Add subsequent costs that are included
  const scRows = (subsequentCosts||[]).filter(sc=>sc.include_in_cost_head==='Y').map(sc=>({
    label: sc.description ? `Subsequent — ${sc.description}` : 'Subsequent Cost',
    val: (parseFloat(sc.transport_cost)||0) + (parseFloat(sc.installation_cost)||0),
    included: true,
    flag: '✅ In Cost Head',
  }));
  const allRows = [...rows, ...scRows];
  const total = allRows.filter(r=>r.included).reduce((s,r)=>s+(r.val||0), 0);
  return (
    <table style={{ width:'100%',borderCollapse:'collapse' }}>
      <thead>
        <tr style={{ borderBottom:'1px solid #e2e8f0' }}>
          <th style={{ padding:'8px 0',textAlign:'left',fontSize:11,fontWeight:700,color:'#64748b',textTransform:'uppercase' }}>Cost Head</th>
          <th style={{ padding:'8px 0',textAlign:'right',fontSize:11,fontWeight:700,color:'#64748b',textTransform:'uppercase' }}>Amount</th>
        </tr>
      </thead>
      <tbody>
        {allRows.filter(r=>r.val).map((row,i)=>(
          <tr key={i} style={{ borderBottom:'1px solid #f1f5f9', opacity: row.included ? 1 : 0.45 }}>
            <td style={{ padding:'7px 0',fontSize:13,color:'#374151' }}>
              {row.label}
              {row.flag && <span style={{ marginLeft:8,fontSize:10,color:row.included?'#16a34a':'#94a3b8' }}>{row.flag}</span>}
            </td>
            <td style={{ padding:'7px 0',fontSize:13,textAlign:'right',fontFamily:'monospace',
              textDecoration: row.included ? 'none' : 'line-through',color: row.included?'inherit':'#94a3b8' }}>
              {fmt2l(row.val)}
            </td>
          </tr>
        ))}
        <tr style={{ borderTop:'2px solid #1a56db' }}>
          <td style={{ padding:'8px 0',fontSize:13,fontWeight:800,color:'#1a56db' }}>Total Capitalised Value</td>
          <td style={{ padding:'8px 0',fontSize:14,fontWeight:800,color:'#1a56db',textAlign:'right',fontFamily:'monospace' }}>{fmt2l(total)}</td>
        </tr>
      </tbody>
    </table>
  );
}

function FinPreviewCard({ form, fin, nowLabel, finValues, isNew, selDepType, depMethods }) {
  const month     = nowLabel || new Date().toLocaleString('en-IN',{month:'short',year:'numeric'});
  const hasFYData = (finValues || []).length > 0;

  /* ── Depreciation Method name ─────────────────────────────── */
  const methodName = selDepType
    ? (depMethods?.find(m => m.id === parseInt(selDepType.method_id))?.description
       || selDepType.method_desc || selDepType.method_code
       || form.depreciation_method?.toUpperCase() || 'SLM')
    : (form.depreciation_method?.toUpperCase() || 'SLM');
  const lifeYrs = parseInt(form.useful_life_years) || fin.life || 0;

  /* ── Depreciation Rate — from master percentage ──────────── */
  const masterRate  = selDepType?.percentage != null
    ? parseFloat(selDepType.percentage) : null;
  const displayRate = masterRate != null ? masterRate.toFixed(2) : fin.depRate;

  /* ── CFY Annual Depreciation estimate ─────────────────────── */
  /* Priority: 1) current FY's planned_dep (saved)
               2) master rate × total
               3) SLM formula (fin.annDep) */
  const now        = new Date();
  const curFYStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const currentFY  = `${curFYStart}-${String(curFYStart+1).slice(-2)}`;
  const curFYFin   = (finValues||[]).find(v => v.financial_year === currentFY) || {};
  const _n = v => (v != null && v !== '' && !isNaN(parseFloat(v))) ? parseFloat(v) : null;

  const cfyAnnDep  = _n(curFYFin.planned_depreciation)
    ?? (masterRate != null && fin.total > 0 ? fin.total * masterRate / 100 : fin.annDep);
  const cfyIsEst   = _n(curFYFin.planned_depreciation) == null;

  /* ── Accumulated Depreciation — sum of all posted deps ───── */
  const accumPosted = (finValues||[])
    .reduce((s, fv) => s + (_n(fv.posted_depreciation) || 0), 0);
  const accumDisplay = hasFYData
    ? (accumPosted > 0 ? fmt2(Math.round(accumPosted)) : '₹0.00')
    : '₹0.00';

  /* ── Current Book Value ────────────────────────────────────── */
  const bookVal = hasFYData
    ? Math.max(0, fin.total - accumPosted)
    : fin.total;

  return (
    <div style={{ background:'#f8fafc',borderRadius:10,padding:'12px 16px',border:'1px solid #e2e8f0' }}>
      <table style={{ width:'100%',borderCollapse:'collapse' }}>
        <tbody>
          <tr><td style={{ padding:'5px 0',color:'#64748b',fontSize:12 }}>Total Capitalised Value</td>
            <td style={{ padding:'5px 0',fontWeight:800,fontSize:13,color:'#1a56db',textAlign:'right',fontFamily:'monospace' }}>
              {fmt2(fin.total)}
            </td></tr>
          <tr><td style={{ padding:'5px 0',color:'#64748b',fontSize:12 }}>Depreciation Method</td>
            <td style={{ padding:'5px 0',fontWeight:600,fontSize:13,textAlign:'right' }}>
              {methodName}{lifeYrs > 0 ? ` — ${lifeYrs} yrs` : ''}
            </td></tr>
          <tr><td style={{ padding:'5px 0',color:'#64748b',fontSize:12 }}>Depreciation Rate</td>
            <td style={{ padding:'5px 0',fontWeight:600,fontSize:13,textAlign:'right' }}>
              {displayRate}% p.a.
              {masterRate != null && <span style={{fontSize:10,color:'#94a3b8',marginLeft:4}}>(Master)</span>}
            </td></tr>
          <tr><td style={{ padding:'5px 0',color:'#64748b',fontSize:12 }}>CFY Annual Depreciation</td>
            <td style={{ padding:'5px 0',fontWeight:600,fontSize:13,textAlign:'right',fontFamily:'monospace',
              color: cfyIsEst ? '#f59e0b' : 'inherit' }}>
              {fin.total > 0 ? fmt2(Math.round(cfyAnnDep)) : '—'}
              {cfyIsEst && fin.total > 0 && <span style={{fontSize:10,color:'#f59e0b',marginLeft:4}}>(est.)</span>}
            </td></tr>
          <tr><td style={{ padding:'5px 0',color:'#64748b',fontSize:12 }}>Accumulated Dep.</td>
            <td style={{ padding:'5px 0',fontWeight:600,fontSize:13,textAlign:'right',fontFamily:'monospace',
              color:'#64748b' }}>
              {accumDisplay}
            </td></tr>
          <tr style={{ borderTop:'2px solid #1a56db' }}>
            <td style={{ padding:'7px 0',fontSize:12,fontWeight:700 }}>Current Book Value ({month})</td>
            <td style={{ padding:'7px 0',fontSize:14,fontWeight:800,color:'#1a56db',textAlign:'right',fontFamily:'monospace' }}>
              {fmt2(Math.round(bookVal))}
            </td>
          </tr>
          <tr><td style={{ padding:'5px 0',color:'#64748b',fontSize:12 }}>End of Useful Life</td>
            <td style={{ padding:'5px 0',fontSize:12,color:'#94a3b8',textAlign:'right' }}>{fin.eol}</td></tr>
        </tbody>
      </table>
      {!hasFYData && (
        <div style={{ marginTop:8, fontSize:11, color:'#94a3b8', lineHeight:1.5,
          borderTop:'1px solid #e2e8f0', paddingTop:8 }}>
          💡 Accumulated Dep. &amp; Current Book Value will reflect actual posted amounts
          once you save entries in <strong>Financial Year Review</strong> (Financial tab).
          <em style={{display:'block',marginTop:3,color:'#f59e0b'}}>
            (est.) values are computed from the Depreciation Type Master rate.
          </em>
        </div>
      )}
    </div>
  );
}

function FinanceViewTab({ profile, fin, nowLabel, assetClasses,
  depTypes, depMethods, financialYears,
  finValues, setFinValues, selFY, setSelFY, fyForm, setFYForm, savingFY, setSavingFY, assetId,
  subsequentCosts }) {

  const month = nowLabel || new Date().toLocaleString('en-IN',{month:'short',year:'numeric'});

  /* ── Actual depreciation method name ─────────────────────── */
  const selDepType = depTypes?.find(dt => dt.id === parseInt(profile?.dep_type_id));
  const methodName = selDepType
    ? (depMethods?.find(m => m.id === parseInt(selDepType.method_id))?.description
       || selDepType.method_desc || selDepType.method_code
       || profile?.depreciation_method?.toUpperCase() || 'SLM')
    : (profile?.depreciation_method?.toUpperCase() || 'SLM');

  /* Dep rate from master — for DISPLAY only */
  const masterRate = selDepType?.percentage != null
    ? parseFloat(selDepType.percentage) : null;
  const displayRate = masterRate != null
    ? masterRate.toFixed(2)
    : fin.depRate;

  /* Current FY */
  const now        = new Date();
  const curFYStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const currentFY  = `${curFYStart}-${String(curFYStart+1).slice(-2)}`;
  const capFYStart = fin.capFYStart;

  /* ── PRIMARY SOURCE: finValues (manually entered by user) ──
     These values are authoritative for ALL calculations.
  ─────────────────────────────────────────────────────────── */
  const curFYFin = finValues?.find(v => v.financial_year === currentFY) || {};

  const _num = (v) => (v != null && v !== '' && !isNaN(parseFloat(v)))
    ? parseFloat(v) : null;

  /* Sum of posted depreciation for all FYs strictly BEFORE `fy`
     (lexical compare works because FY codes are "YYYY-YY") */
  const cumPostedBefore = (fy) =>
    (finValues || [])
      .filter(fv => fv.financial_year < fy)
      .reduce((s, fv) => s + (_num(fv.posted_depreciation) || 0), 0);

  /* Has the user saved ANY Financial Year Review data for this asset? */
  const hasFYData = (finValues || []).length > 0;

  /* ── Opening Book Value of the Current FY ─────────────────────
     = Total − ΣPosted depreciation for all FYs strictly before CFY.
     This is a carry-forward value — always computable once at least
     one prior FY has been saved (even if CFY itself has no entry yet).
  ─────────────────────────────────────────────────────────── */
  const cumBeforeCFY   = cumPostedBefore(currentFY);
  const openingBV_CFY  = hasFYData ? Math.max(0, fin.total - cumBeforeCFY) : null;

  /* Raw manually-entered values for the Current FY (may be absent) */
  const cfyPostedDepRaw  = _num(curFYFin.posted_depreciation);
  const cfyPlannedDepRaw = _num(curFYFin.planned_depreciation);

  /* CFY Annual Planned Depreciation
     = manually entered value, else an ESTIMATE using the
     Depreciation Type Master rate applied to the opening BV. */
  const cfyPlannedDep = cfyPlannedDepRaw != null
    ? cfyPlannedDepRaw
    : (masterRate != null && openingBV_CFY != null
        ? openingBV_CFY * masterRate / 100
        : null);
  const cfyPlannedIsEstimate = cfyPlannedDepRaw == null && cfyPlannedDep != null;

  /* "Accumulated Depreciation (CFY)" — only the manually-posted value;
     stays "— Not posted yet" if the user hasn't entered it. */
  const cfyPostedDep = cfyPostedDepRaw;

  /* Current Book Value row = Planned Ordinary Depreciation (CFY) */
  const currentBV  = cfyPlannedDep;

  /* Net Book Value on 31-Mar row = Posted/Recorded Ordinary Depreciation (CFY) */
  const nbvYearEnd = cfyPostedDep;

  /* ── FY Schedule — only up to current FY ─────────────────── */
  const fySchedule = [];
  if (capFYStart != null) {
    for (let i = 0; i < (fin.life || 10); i++) {
      const y = capFYStart + i;
      if (y > curFYStart) break;
      fySchedule.push(`${y}-${String(y+1).slice(-2)}`);
    }
  }

  /* ── For each FY in schedule: compute Planned/Posted NBV via the
     cumulative-depreciation formula (matches Excel exactly).
     If no Financial Year Review entry exists for `fy`, both are null
     (i.e. "no values" until the user saves that FY's review).
  ─────────────────────────────────────────────────────────── */
  const getScheduleRow = (fy) => {
    const fv = finValues?.find(v => v.financial_year === fy);
    if (!fv) return { plannedDep: null, postedDep: null };
    return {
      plannedDep: _num(fv.planned_depreciation),
      postedDep:  _num(fv.posted_depreciation),
    };
  };

  /* FY dropdown — range: capitalise FY → current FY (inclusive) */
  const fyDropdownList = financialYears?.length > 0
    ? financialYears
        .map(fy => fy.code)
        .filter(code => {
          const y = parseInt(code.split('-')[0]);
          if (y > curFYStart) return false;
          if (capFYStart != null && y < capFYStart) return false;
          return true;
        })
    : (() => {
        const list = [];
        const startY = capFYStart != null ? capFYStart : curFYStart;
        for (let y = startY; y <= curFYStart; y++) {
          list.push(`${y}-${String(y+1).slice(-2)}`);
        }
        return list;
      })();

  /* ── FY change handler ── */
  const handleFYChange = (fy) => {
    setSelFY(fy);
    const saved = finValues.find(v => v.financial_year === fy);
    if (saved) {
      setFYForm(saved);
    } else {
      /* Default blank form — user will manually enter values */
      setFYForm({
        financial_year:       fy,
        acquisition_cost:     fin.total ? Math.round(fin.total).toString() : '',
        planned_depreciation: '',
        posted_depreciation:  '',
        nbv_year_end:         '',
        nbv_current_month:    '',
      });
    }
  };

  const saveFY = async () => {
    if (!selFY) return;
    setSavingFY(true);
    try {
      /* Auto-compute NBV fields from cumulative-depreciation formula
         (matches Excel) before saving — user doesn't enter these. */
      const before     = cumPostedBefore(selFY);
      const plannedDep = _num(fyForm.planned_depreciation);
      const postedDep  = _num(fyForm.posted_depreciation);
      const nbvYearEndCalc = plannedDep != null
        ? Math.max(0, fin.total - before - plannedDep) : '';
      const nbvCurMonthCalc = postedDep != null
        ? Math.max(0, fin.total - before - postedDep) : '';

      const payload = {
        ...fyForm,
        financial_year:    selFY,
        nbv_year_end:      nbvYearEndCalc !== '' ? nbvYearEndCalc.toFixed(2) : '',
        nbv_current_month: nbvCurMonthCalc !== '' ? nbvCurMonthCalc.toFixed(2) : '',
      };
      const r = await mastersAPI.upsertFinancialValues(assetId, payload);
      /* Use server response if it returns the saved row, else use payload */
      const savedRow = (r?.data && typeof r.data === 'object' && r.data.financial_year)
        ? r.data
        : payload;

      /* Update local finValues immediately — no page refresh needed */
      setFinValues(prev => {
        const exists = prev.some(v => v.financial_year === selFY);
        return exists
          ? prev.map(v => v.financial_year === selFY ? savedRow : v)
          : [...prev, savedRow];
      });
      /* Keep the form in sync with the saved row */
      setFYForm(savedRow);

      toast.success('Financial values saved');
    } catch { toast.error('Save failed'); }
    finally { setSavingFY(false); }
  };

  const setFV = (k, v) => setFYForm(f => ({ ...f, [k]: v }));
  /* ₹ with Indian thousand separators, 2 decimals */
  const fmtIn = n => (n !== '' && n != null && !isNaN(parseFloat(n)))
    ? `₹${Number(n).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2})}`
    : '—';
  const IS2 = { border:'1.5px solid #e2e8f0',borderRadius:7,padding:'7px 10px',
    fontSize:13,outline:'none',background:'#fff',boxSizing:'border-box',width:'100%' };
  const selAC = assetClasses?.find(ac => ac.id === parseInt(profile.asset_class_id));

  return (
    <div>
      {/* Asset Class info banner */}
      {selAC && (
        <div style={{ background:'#f0f6ff',borderRadius:10,padding:'10px 16px',
          marginBottom:16,fontSize:13,color:'#1a56db',display:'flex',gap:12,flexWrap:'wrap' }}>
          <span>🏷 <strong>Asset Class:</strong> {selAC.code} — {selAC.description}</span>
          <span>·</span>
          <span><strong>Dep. Area:</strong> {selAC.dep_area}</span>
          <span>·</span>
          <span><strong>Useful Life:</strong> {selAC.useful_life_months} months</span>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>
        {/* LEFT column */}
        <div>
          <div style={SECT}>COST SUMMARY<div style={{ flex:1,height:1,background:'#e2e8f0',marginLeft:4 }}/></div>
          <div style={{ border:'1px solid #e2e8f0',borderRadius:10,overflow:'hidden',marginBottom:20 }}>
            <CostTable fin={fin} profile={profile} subsequentCosts={subsequentCosts}/>
          </div>

          <div style={SECT}>DEPRECIATION & BOOK VALUE<div style={{ flex:1,height:1,background:'#e2e8f0',marginLeft:4 }}/></div>
          {!hasFYData ? (
            <div style={{ border:'1px dashed #e2e8f0',borderRadius:10,padding:'24px 16px',
              textAlign:'center', color:'#94a3b8', fontSize:13, marginBottom:0 }}>
              No Financial Year Review data saved yet.<br/>
              <span style={{ fontSize:12 }}>Enter and save values below to see Depreciation &amp; Book Value.</span>
            </div>
          ) : (
          <div style={{ border:'1px solid #e2e8f0',borderRadius:10,overflow:'hidden' }}>
            <table style={{ width:'100%',borderCollapse:'collapse' }}>
              <tbody>
                {[
                  ['Depreciation Method',              `${methodName} — ${fin.life} yrs`],
                  ['CFY Annual Planned Depreciation',  cfyPlannedDep != null
                    ? `${fmt2(Math.round(cfyPlannedDep))}${cfyPlannedIsEstimate ? ' (estimated)' : ''}`
                    : '—'],
                  ['Depreciation Rate',                `${displayRate}% p.a.`],
                  ['Accumulated Depreciation (CFY)',   cfyPostedDep != null ? fmt2(Math.round(cfyPostedDep)) : '— Not posted yet'],
                ].map(([l,v])=>(
                  <tr key={l} style={{ borderBottom:'1px solid #f1f5f9' }}>
                    <td style={{ padding:'9px 14px',fontSize:13,color:'#64748b' }}>{l}</td>
                    <td style={{ padding:'9px 14px',fontSize:13,fontWeight:600,textAlign:'right' }}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ padding:'10px 14px',background:'#f0f6ff',borderTop:'2px solid #1a56db',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
              <div>
                <div style={{ fontSize:13,fontWeight:700 }}>Current Book Value ({month})</div>
                <div style={{ fontSize:10,color:'#94a3b8',marginTop:1 }}>Planned Ordinary Depreciation {currentFY}</div>
              </div>
              <span style={{ fontSize:16,fontWeight:800,color:'#1a56db',fontFamily:'monospace' }}>
                {currentBV != null ? fmt2(Math.round(currentBV)) : '—'}
              </span>
            </div>
            <div style={{ padding:'8px 14px',background:'#f8fafc',borderTop:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
              <div>
                <div style={{ fontSize:12,color:'#64748b' }}>Net Book Value on 31-Mar-{curFYStart+1}</div>
                <div style={{ fontSize:10,color:'#94a3b8',marginTop:1 }}>Posted Ordinary Depreciation {currentFY}</div>
              </div>
              <span style={{ fontSize:13,fontWeight:700,color:'#374151',fontFamily:'monospace' }}>
                {nbvYearEnd != null ? fmt2(Math.round(nbvYearEnd)) : '—'}
              </span>
            </div>
            <div style={{ padding:'8px 14px',borderTop:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between',fontSize:12,color:'#64748b' }}>
              <span>End of Useful Life</span><span>{fin.eol}</span>
            </div>
          </div>
          )}
        </div>

        {/* RIGHT column */}
        <div>
          {/* ══ DEPRECIATION SCHEDULE (PLANNED vs POSTED) ══ */}
          <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8 }}>
            <div style={{ ...SECT, marginBottom:0, flex:1 }}>
              DEPRECIATION SCHEDULE (PLANNED vs POSTED)
              <div style={{ flex:1,height:1,background:'#e2e8f0',marginLeft:4 }}/>
            </div>
            {/* Legend */}
            <div style={{ display:'flex',alignItems:'center',gap:12,marginLeft:12,flexShrink:0 }}>
              <div style={{ display:'flex',alignItems:'center',gap:5 }}>
                <div style={{ width:18,height:10,borderRadius:3,background:'#bfdbfe' }}/>
                <span style={{ fontSize:10,color:'#64748b',fontWeight:600 }}>Planned</span>
              </div>
              <div style={{ display:'flex',alignItems:'center',gap:5 }}>
                <div style={{ width:18,height:10,borderRadius:3,background:'#1a56db' }}/>
                <span style={{ fontSize:10,color:'#64748b',fontWeight:600 }}>Posted</span>
              </div>
            </div>
          </div>

          {!hasFYData ? (
            <div style={{ border:'1px dashed #e2e8f0',borderRadius:10,padding:'24px 16px',
              textAlign:'center', color:'#94a3b8', fontSize:13, marginBottom:24 }}>
              No Financial Year Review data saved yet.<br/>
              <span style={{ fontSize:12 }}>Planned and Posted values will appear here once you save the Financial Year Review.</span>
            </div>
          ) : fySchedule.length===0
            ? <div style={{ fontSize:13,color:'#94a3b8',marginBottom:20 }}>
                Enter Capitalised Date &amp; cost to see schedule.
              </div>
            : (
            <div style={{ border:'1px solid #e2e8f0',borderRadius:10,overflow:'hidden',marginBottom:24 }}>
              {/* Column headers */}
              <div style={{ display:'grid',gridTemplateColumns:'60px 1fr 120px',
                gap:0,background:'#f8fafc',borderBottom:'1px solid #e2e8f0',
                padding:'8px 14px',alignItems:'center' }}>
                <span style={{ fontSize:10,fontWeight:700,color:'#94a3b8',
                  textTransform:'uppercase',letterSpacing:'.06em' }}>YEAR</span>
                <span style={{ fontSize:10,fontWeight:700,letterSpacing:'.06em',
                  paddingLeft:8 }}>
                  <span style={{ color:'#93c5fd' }}>PLANNED</span>
                  <span style={{ color:'#64748b' }}> vs </span>
                  <span style={{ color:'#1a56db' }}>POSTED</span>
                </span>
                <span style={{ fontSize:10,fontWeight:700,color:'#94a3b8',
                  textTransform:'uppercase',letterSpacing:'.06em',textAlign:'right' }}>
                  AMOUNT (₹)
                </span>
              </div>

              {/* Rows */}
              {/* Bar widths are relative to the largest planned dep across all FYs */}
              {(() => {
                const maxDep = Math.max(1, ...fySchedule.map(fy => {
                  const { plannedDep } = getScheduleRow(fy);
                  return plannedDep ?? 0;
                }));
                return fySchedule.map((fy, idx) => {
                const { plannedDep, postedDep } = getScheduleRow(fy);
                const plannedPct = plannedDep != null ? Math.min(100,(plannedDep/maxDep)*100) : 0;
                const postedPct  = postedDep  != null ? Math.min(100,(postedDep /maxDep)*100) : 0;
                const isCur  = fy === currentFY;
                const isPast = capFYStart != null && idx < (curFYStart - capFYStart);
                const isFut  = !isCur && !isPast;

                /* Colour scheme — blue for past/current, teal for future */
                const planBg  = isFut ? '#99f6e4' : '#bfdbfe';
                const postBg  = isFut ? '#0d9488' : '#1a56db';
                const yearClr = isCur ? '#1a56db' : (isPast ? '#374151' : '#94a3b8');

                return (
                  <div key={fy} style={{
                    display:'grid', gridTemplateColumns:'60px 1fr 120px',
                    gap:0, padding:'10px 14px', alignItems:'center',
                    borderBottom:'1px solid #f1f5f9',
                    background: isCur ? '#f0f6ff' : 'transparent',
                  }}>
                    {/* Year label */}
                    <span style={{ fontSize:12,width:67, fontWeight:isCur?800:500,
                      color:yearClr, lineHeight:1.3 }}>
                      {fy}{isCur ? ' ★' : ''}
                    </span>

                    {/* Dual bar chart */}
                    <div style={{ paddingLeft:8, display:'flex',
                      flexDirection:'column', gap:4 }}>
                      {/* Planned bar */}
                      <div style={{ display:'flex',alignItems:'center',gap:6 }}>
                        <div style={{ flex:1,height:8,background:'#f1f5f9',
                          borderRadius:4,overflow:'hidden' }}>
                          <div style={{ height:'100%', width:`${plannedPct}%`,
                            background:planBg, borderRadius:4,
                            transition:'width .4s ease' }}/>
                        </div>
                      </div>
                      {/* Posted bar */}
                      <div style={{ display:'flex',alignItems:'center',gap:6 }}>
                        <div style={{ flex:1,height:8,background:'#f1f5f9',
                          borderRadius:4,overflow:'hidden' }}>
                          <div style={{ height:'100%',
                            width:`${postedDep != null ? postedPct : 0}%`,
                            background: postedDep != null ? postBg : 'transparent',
                            borderRadius:4, opacity: postedDep!=null?1:.35,
                            transition:'width .4s ease' }}/>
                        </div>
                      </div>
                    </div>

                    {/* Amounts */}
                    <div style={{ textAlign:'right', lineHeight:1.5 }}>
                      {/* Planned amount */}
                      <div style={{ fontSize:11, color:'#64748b', fontFamily:'monospace' }}>
                        {plannedDep != null ? fmt2(Math.round(plannedDep)) : '—'}
                      </div>
                      {/* Posted amount */}
                      <div style={{ fontSize:11, fontWeight:700,
                        color: postedDep!=null ? postBg : '#cbd5e1',
                        fontFamily:'monospace' }}>
                        {postedDep!=null ? fmt2(Math.round(postedDep)) : '— not posted'}
                      </div>
                    </div>
                  </div>
                );
              })
              })()}
            </div>
          )}

          {/* ── FINANCIAL YEAR REVIEW ─── */}
          <div style={SECT}>FINANCIAL YEAR REVIEW<div style={{ flex:1,height:1,background:'#e2e8f0',marginLeft:4 }}/></div>
          <div style={{ background:'#f8fafc',borderRadius:10,border:'1px solid #e2e8f0',padding:16 }}>
            <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:14 }}>
              <label style={{ fontSize:12,fontWeight:600,color:'#475569',whiteSpace:'nowrap' }}>Financial Year</label>
              <select value={selFY} onChange={e=>handleFYChange(e.target.value)}
                style={{ ...IS2,width:120 }}>
                <option value="">— Select —</option>
                {fyDropdownList.map(fy=><option key={fy} value={fy}>{fy}</option>)}
              </select>
              {selFY && (
                <button onClick={saveFY} disabled={savingFY}
                  style={{ padding:'6px 14px',background:'#1a56db',color:'#fff',border:'none',
                    borderRadius:7,fontSize:12,fontWeight:600,cursor:'pointer',whiteSpace:'nowrap' }}>
                  {savingFY?'Saving…':'💾 Save'}
                </button>
              )}
            </div>

            {selFY && (() => {
              /* Live-preview NBV calcs as user types (before save) */
              const before     = cumPostedBefore(selFY);
              const plannedDep = _num(fyForm.planned_depreciation);
              const postedDep  = _num(fyForm.posted_depreciation);
              const liveNbvYearEnd  = plannedDep != null ? Math.max(0, fin.total - before - plannedDep) : null;
              const liveNbvCurMonth = postedDep  != null ? Math.max(0, fin.total - before - postedDep)  : null;

              return (
              <div style={{ display:'flex',flexDirection:'column',gap:8 }}>
                {/* ── Editable inputs ── */}
                {[
                  {label:'Acquisition and Production Costs', key:'acquisition_cost'},
                  {label:`Planned Ordinary Depreciation ${selFY}`, key:'planned_depreciation'},
                  {label:`Posted Ordinary Depreciation ${selFY}`, key:'posted_depreciation'},
                ].map(row=>(
                  <div key={row.key} style={{ display:'flex',alignItems:'center',
                    justifyContent:'space-between',padding:'8px 12px',
                    background:'#fff',borderRadius:7,border:'1px solid #f1f5f9' }}>
                    <span style={{ fontSize:13,color:'#475569',flex:1 }}>{row.label}</span>
                    <div style={{ display:'flex',alignItems:'center',gap:6 }}>
                      <span style={{ fontSize:12,color:'#94a3b8' }}>₹</span>
                      <input type="number"
                        value={fyForm[row.key]||''}
                        onChange={e=>setFV(row.key,e.target.value)}
                        placeholder="0.00"
                        style={{ border:'1px solid #e2e8f0',borderRadius:5,padding:'4px 8px',
                          fontSize:13,fontFamily:'monospace',width:130,textAlign:'right',
                          background:'#fff',outline:'none' }}/>
                      <span style={{ fontSize:12,color:'#1a56db',fontFamily:'monospace',fontWeight:600,width:130,textAlign:'right' }}>
                        {fmtIn(fyForm[row.key])}
                      </span>
                    </div>
                  </div>
                ))}

                {/* ── Auto-calculated NBV (read-only) ── */}
                {[
                  {
                    label: `Net Book Value on ${selFY.split('-')[1]?`31/03/20${selFY.split('-')[1]}`:'Year End'}`,
                    hint:  'Opening BV − Planned Dep',
                    val:   liveNbvYearEnd,
                  },
                  /* {
                    label: 'Net Book Value as of Current Month',
                    hint:  'Opening BV − Recorded Dep',
                    val:   liveNbvCurMonth,
                  }, */
                ].map(row=>(
                  <div key={row.label} style={{ display:'flex',alignItems:'center',
                    justifyContent:'space-between',padding:'8px 12px',
                    background:'#f0f6ff',borderRadius:7,border:'1px solid #dbeafe' }}>
                    <div>
                      <div style={{ fontSize:13,color:'#475569' }}>
                        {row.label}
                        <span style={{ fontSize:10,color:'#94a3b8',marginLeft:6,fontWeight:400 }}>(auto-calculated)</span>
                      </div>
                      <div style={{ fontSize:10,color:'#94a3b8',marginTop:1 }}>{row.hint}</div>
                    </div>
                    <span style={{ fontSize:13,color:'#1a56db',fontFamily:'monospace',fontWeight:700,minWidth:130,textAlign:'right' }}>
                      {row.val != null ? fmtIn(row.val.toFixed(2)) : '—'}
                    </span>
                  </div>
                ))}
              </div>
              );
            })()}
            {!selFY && <div style={{ fontSize:13,color:'#94a3b8',textAlign:'center',padding:'16px 0' }}>Select a Financial Year to view / edit values</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function WOTypeBadge({ type }) {
  const M={pm:'#dbeafe:#1a56db',breakdown:'#fee2e2:#dc2626',inspection:'#f0fdf4:#15803d',repair:'#fef3c7:#b45309',calibration:'#ede9fe:#7c3aed'};
  const [bg,c]=(M[type]||'#f1f5f9:#64748b').split(':');
  return <span style={{ padding:'2px 8px',borderRadius:4,fontSize:11,fontWeight:500,background:bg,color:c }}>{(type||'').toUpperCase()}</span>;
}
function StatusChip({ status }) {
  const M={open:['#fef3c7','#d97706'],in_progress:['#dbeafe','#1a56db'],completed:['#dcfce7','#16a34a'],closed:['#dcfce7','#16a34a'],cancelled:['#f1f5f9','#64748b']};
  const [bg,c]=M[status]||['#f1f5f9','#64748b'];
  return <Chip bg={bg} c={c}>{(status||'').replace(/_/g,' ').toUpperCase()}</Chip>;
}
function EmptyState({ icon, text }) {
  return <div style={{ textAlign:'center',color:'#94a3b8',padding:'36px 0',fontSize:13 }}><div style={{ fontSize:32,marginBottom:8 }}>{icon}</div>{text}</div>;
}
function WOCard({ wo }) {
  const dot={pm:'#1a56db',breakdown:'#dc2626',inspection:'#15803d',repair:'#d97706',calibration:'#7c3aed'}[wo.type]||'#94a3b8';
  return (
    <div style={{ border:`1px solid ${wo.type==='breakdown'?'rgba(220,38,38,.15)':'#e2e8f0'}`,borderLeft:`3px solid ${dot}`,borderRadius:10,padding:'14px 16px',marginBottom:10,background:wo.type==='breakdown'?'#fff8f8':'#fff' }}>
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8 }}>
        <div>
          <div style={{ fontSize:13,fontWeight:700,color:'#1a1f2e',marginBottom:3 }}>{wo.title||wo.wo_number}</div>
          <div style={{ fontSize:11,color:'#94a3b8',display:'flex',gap:8 }}>
            <span style={{ fontFamily:'monospace',color:'#1a56db' }}>{wo.wo_number}</span>
            {wo.assigned_name&&<span>· {wo.assigned_name}</span>}
          </div>
        </div>
        <div style={{ display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4 }}>
          <StatusChip status={wo.status}/>
          <span style={{ fontSize:11,color:'#94a3b8' }}>{wo.created_at?.slice?.(0,10)}</span>
        </div>
      </div>
    </div>
  );
}
function LifecycleBar({ asset }) {
  const stages=[
    {label:'Acquired',  done:!!asset.purchase_date,    date:asset.purchase_date?.slice(0,7)},
    {label:'Deployed',  done:!!asset.installation_date,date:asset.installation_date?.slice(0,7)},
    {label:'In Service',active:asset.status==='operational',date:'Active'},
    {label:'Upgrade',   done:false,date:'Future'},
    {label:'Disposal',  done:false,date:'Future'},
  ];
  return (
    <div style={{ display:'flex',alignItems:'center',marginBottom:16,overflowX:'auto' }}>
      {stages.map((s,i)=>(
        <div key={s.label} style={{ display:'flex',alignItems:'center',flex:1,minWidth:60 }}>
          <div style={{ display:'flex',flexDirection:'column',alignItems:'center',flex:'none' }}>
            <div style={{ width:26,height:26,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,border:'2px solid',
              background:s.active?'#1a56db':s.done?'#dcfce7':'#fff',borderColor:s.active?'#1a56db':s.done?'#16a34a':'#cbd5e1',
              color:s.active?'#fff':s.done?'#16a34a':'#94a3b8',boxShadow:s.active?'0 0 0 4px rgba(26,86,219,.12)':'none' }}>
              {s.active?'↻':s.done?'✓':'○'}
            </div>
            <div style={{ fontSize:9,fontWeight:700,marginTop:4,textAlign:'center',color:s.active?'#1a56db':s.done?'#16a34a':'#94a3b8' }}>{s.label}</div>
            {s.date&&<div style={{ fontSize:8,color:'#94a3b8',marginTop:1 }}>{s.date}</div>}
          </div>
          {i<stages.length-1&&<div style={{ flex:1,height:2,background:s.done?'#16a34a':'#e2e8f0',margin:'0 2px',marginBottom:20 }}/>}
        </div>
      ))}
    </div>
  );
}

/* ─── WO Action Modal ─────────────────────────────────────── */
const WO_CFG = {
  breakdown: {
    icon: '⚡', title: 'Report Breakdown',
    badge: { bg:'#fee2e2', c:'#dc2626', text:'Emergency Breakdown Report',
      desc:'This will immediately create a critical WO and notify the maintenance team and plant manager.' },
    type: 'breakdown', pri: 'critical', typeLabel: 'Breakdown / Emergency',
  },
  pm: {
    icon: '🔧', title: 'Schedule Preventive Maintenance',
    badge: { bg:'#dbeafe', c:'#1a56db', text:'Preventive Maintenance Request',
      desc:'This will schedule a PM work order and notify the assigned technician on the due date.' },
    type: 'pm', pri: 'medium', typeLabel: 'Preventive Maintenance (PM)',
  },
  inspection: {
    icon: '🔍', title: 'Request Inspection',
    badge: { bg:'#f0fdf4', c:'#15803d', text:'Inspection Request',
      desc:'A scheduled inspection work order will be created and routed for approval.' },
    type: 'inspection', pri: 'medium', typeLabel: 'Inspection',
  },
  repair: {
    icon: '✱', title: 'Raise Repair Work Order',
    badge: { bg:'#fef3c7', c:'#b45309', text:'Repair Work Order',
      desc:'This repair WO will be routed through the approval workflow before assignment.' },
    type: 'repair', pri: 'high', typeLabel: 'Repair Work Order',
  },
};

function WOActionModal({ type, asset, users, frequencies = [], maintenanceTypes = [], failureTypes = [], onClose, onCreated }) {
  const cfg = WO_CFG[type] || WO_CFG.breakdown;
  const isPM         = type === 'pm';
  const isBreakdown  = type === 'breakdown';
  const defaultDue   = new Date(Date.now() + 7*864e5).toISOString().slice(0,10);

  const [maintenanceTypeId, setMaintenanceTypeId] = useState(() => {
    /* Auto-select first type that matches this WO type */
    const WO_FIELD = { breakdown:'is_breakdown', pm:'is_schedule_pm',
      inspection:'is_inspection', repair:'is_repair' };
    const field = WO_FIELD[type];
    if (!field || !maintenanceTypes?.length) return '';
    const match = maintenanceTypes.find(m => m.is_active && (m[field] === 1 || m[field] === true));
    return match ? String(match.id) : '';
  });

  //const [maintenanceTypeId, setMaintenanceTypeId] = useState('');
  const [priority,          setPriority]          = useState(cfg.pri);
  const [dueDate,           setDueDate]           = useState(defaultDue);
  const [dueTime,           setDueTime]           = useState('09:00');
  const [assignTo,    setAssignTo]    = useState(
    String(asset?.assigned_to || asset?.responsible_person || asset?.employee_id || '')
  );
  const [duration,          setDuration]          = useState('');
  const [cost,              setCost]              = useState('0');
  const [desc,              setDesc]              = useState('');
  const [checklist,         setChecklist]         = useState('');
  const [spareParts,        setSpareParts]        = useState('');
  const [notify,            setNotify]            = useState('Technician + Manager');
  const [saving,            setSaving]            = useState(false);
  /* Breakdown ID — fetched from WO Numbering Series master */
  const [breakdownId, setBreakdownId] = useState('');

  useEffect(() => {
    if (!isBreakdown) return;
    // Fetch next number preview (no increment)
    const token = localStorage.getItem('iassets_token');
    const API   = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/$/, '');
    fetch(`${API}/masters/wo-numbering-series/next-number`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => d?.next_number ? setBreakdownId(d.next_number) : setBreakdownId(''))
      .catch(() => setBreakdownId(''));
  }, [isBreakdown]);
  /* Breakdown-specific fields */
  const [failureTypeId,  setFailureTypeId]  = useState('');
  const [reportedBy,     setReportedBy]     = useState('');
  const [contactNumber,  setContactNumber]  = useState('');
  /* PM-only fields */
  const [frequencyId,  setFrequencyId]  = useState('');
  const [nextDueDate,  setNextDueDate]  = useState('');
  const [vendorType,   setVendorType]   = useState('In-House Team');
  const [reminderDays, setReminderDays] = useState('3');

  const ii = {
    width:'100%', border:'1px solid #cbd5e1', borderRadius:6,
    padding:'8px 10px', fontSize:13, outline:'none',
    boxSizing:'border-box', background:'#fff',
  };
  const lbl = { fontSize:12, color:'#374151', fontWeight:500, display:'block', marginBottom:4 };

  const submit = async () => {
    if (!desc.trim()) { toast.error('Description is required'); return; }
    setSaving(true);
    try {
      await workOrdersAPI.create({
        title:              `${cfg.typeLabel} — ${asset?.asset_code}`,
        asset_id:           asset.id,
        type:               cfg.type,
        maintenance_type_id: maintenanceTypeId ? parseInt(maintenanceTypeId) : null,
        priority,
        due_date:           dueDate ? dueDate.slice(0,10) : null,
        assigned_to:        assignTo ? parseInt(assignTo) : null,
        estimated_cost:     parseFloat(cost) || 0,
        description:        desc,
        checklist:          checklist || null,
        spare_parts_req:    spareParts || null,
        notify:             notify || null,
        estimated_duration: duration || null,
        /* Breakdown-specific */
        ...(isBreakdown && {
          failure_type_id:  failureTypeId ? parseInt(failureTypeId) : null,
          reported_by:      reportedBy  || null,
          contact_number:   contactNumber || null,
        }),
        /* PM-specific */
        ...(isPM && {
          frequency_id:   frequencyId ? parseInt(frequencyId) : null,
          next_due_date:  nextDueDate || null,
          vendor_type:    vendorType  || null,
          reminder_days:  reminderDays ? parseInt(reminderDays) : null,
        }),
      });
      toast.success('Work order created successfully!');
      onCreated();
    } catch(e) {
      toast.error(e?.response?.data?.detail || 'Failed to create work order');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(10,14,26,.6)',
      backdropFilter:'blur(4px)', zIndex:1300,
      display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'#fff', borderRadius:14, width:520, maxWidth:'96vw',
        maxHeight:'92vh', display:'flex', flexDirection:'column',
        boxShadow:'0 24px 64px rgba(0,0,0,.22)', overflow:'hidden' }}>

        {/* ── Header ── */}
        <div style={{ padding:'16px 20px 12px', borderBottom:'1px solid #e2e8f0',
          display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:16, fontWeight:700, color:'#1a1f2e' }}>
              {cfg.icon} {cfg.title} — {asset?.asset_code}
            </div>
            <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>
              Asset: {asset?.name} — {asset?.asset_code}
            </div>
          </div>
          <button onClick={onClose}
            style={{ background:'none', border:'none', cursor:'pointer',
              fontSize:18, color:'#94a3b8', padding:'0 4px' }}>✕</button>
        </div>

        {/* ── Scrollable body ── */}
        <div style={{ flex:1, overflowY:'auto', padding:'16px 20px',
          display:'flex', flexDirection:'column', gap:14 }}>

          {/* Info badge */}
          <div style={{ background:cfg.badge.bg, borderRadius:8, padding:'10px 14px',
            display:'flex', gap:10, alignItems:'flex-start' }}>
            <span style={{ fontSize:14, marginTop:1 }}>ℹ</span>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:cfg.badge.c }}>{cfg.badge.text}</div>
              <div style={{ fontSize:12, color:cfg.badge.c, marginTop:2, opacity:.85 }}>{cfg.badge.desc}</div>
            </div>
          </div>

          {/* Breakdown ID — from WO Numbering Series */}
          {isBreakdown && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
              background:'#f0f6ff', border:'1px solid #bfdbfe',
              borderRadius:8, padding:'10px 14px', marginBottom:4 }}>
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:'#1a56db',
                  textTransform:'uppercase', letterSpacing:'.06em' }}>
                  Breakdown ID
                </div>
                <div style={{ fontSize:13, color:'#94a3b8', marginTop:1 }}>
                  Auto-assigned from WO Numbering Series
                </div>
              </div>
              <div style={{ fontSize:20, fontWeight:800, color:'#1a56db',
                fontFamily:'monospace', letterSpacing:'.04em' }}>
                {breakdownId || <span style={{ fontSize:13, color:'#94a3b8' }}>— Not configured —</span>}
              </div>
            </div>
          )}
          {/* Ticket Type — dropdown filtered by WO type */}
          {(() => {
            /* Map WO type → DB column that flags applicable maintenance types */
            const WO_FIELD = {
              breakdown:  'is_breakdown',
              pm:         'is_schedule_pm',
              inspection: 'is_inspection',
              repair:     'is_repair',
            };
            const field = WO_FIELD[type];
            const filtered = maintenanceTypes.filter(m => {
              if (!m.is_active) return false;
              if (!field) return true;                  // unknown type → show all
              return m[field] === 1 || m[field] === true;
            });
            return (
              <div>
                <label style={lbl}>Ticket Type / Maintenance Type</label>
                {filtered.length > 0 ? (
                  <select value={maintenanceTypeId}
                    onChange={e => setMaintenanceTypeId(e.target.value)}
                    style={ii}>
                    <option value="">— Select Maintenance Type —</option>
                    {filtered.map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                ) : maintenanceTypes.length === 0 ? (
                  /* master not loaded yet */
                  <select disabled style={{ ...ii, background:'#f8fafc', color:'#64748b' }}>
                    <option>{cfg.typeLabel}</option>
                  </select>
                ) : (
                  /* master loaded but no types match this WO type */
                  <select disabled style={{ ...ii, background:'#fff8ed', color:'#b45309' }}>
                    <option>— No types configured for {cfg.title} —</option>
                  </select>
                )}
                {filtered.length === 0 && maintenanceTypes.length > 0 && (
                  <div style={{ fontSize:11, color:'#f59e0b', marginTop:4 }}>
                    ⚠ Open <strong>Setup → Maintenance Type Master</strong> and
                    tick <strong>"{
                      { breakdown:'Breakdown', pm:'Schedule PM',
                        inspection:'Inspection', repair:'Repair Work' }[type]
                    }"</strong> for the relevant types.
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── BREAKDOWN-SPECIFIC FIELDS ────────────────────── */}
          {isBreakdown && (
            <div style={{ background:'#fff8f8', borderRadius:10, padding:'14px',
              border:'1px solid #fecaca', display:'flex', flexDirection:'column', gap:12 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#dc2626',
                textTransform:'uppercase', letterSpacing:'.06em' }}>
                ⚡ Breakdown Details
              </div>

              {/* Failure Type */}
              {failureTypes.length > 0 && (
                <div>
                  <label style={lbl}>Failure Type</label>
                  <select value={failureTypeId}
                    onChange={e => setFailureTypeId(e.target.value)} style={ii}>
                    <option value="">— Select Failure Type —</option>
                    {failureTypes.filter(f => f.is_active !== false).map(f => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Reported By + Contact Number */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={lbl}>Reported By</label>
                  <input value={reportedBy}
                    onChange={e => setReportedBy(e.target.value)}
                    placeholder="Name of reporter" style={ii}/>
                </div>
                <div>
                  <label style={lbl}>Contact Number</label>
                  <input value={contactNumber}
                    onChange={e => setContactNumber(e.target.value)}
                    placeholder="+91 98765 43210" style={ii}/>
                </div>
              </div>
            </div>
          )}

          {/* ── PM-ONLY FIELDS ─────────────────────────────── */}
          {isPM && (
            <div style={{ background:'#f0f9ff', borderRadius:10, padding:'14px',
              border:'1px solid #bae6fd', display:'flex', flexDirection:'column', gap:12 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#0369a1',
                textTransform:'uppercase', letterSpacing:'.06em' }}>
                🔧 PM Configuration
              </div>

              {/* Frequency + Reminder */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={lbl}>Frequency</label>
                  <select value={frequencyId} onChange={e=>setFrequencyId(e.target.value)} style={ii}>
                    <option value="">— Select Frequency —</option>
                    {frequencies.filter(f=>f.is_active).map(f=>(
                      <option key={f.id} value={f.id}>
                        {f.name}{f.days_interval ? ` (${f.days_interval} days)` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Reminder (days before)</label>
                  <input type="number" min="0" value={reminderDays}
                    onChange={e=>setReminderDays(e.target.value)}
                    placeholder="e.g. 3"
                    style={{ ...ii }}/>
                </div>
              </div>

              {/* Next Due Date + Vendor / Service Partner */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={lbl}>Next Due Date</label>
                  <input type="date" value={nextDueDate}
                    onChange={e=>setNextDueDate(e.target.value)}
                    style={{ ...ii }}/>
                </div>
                <div>
                  <label style={lbl}>Vendor / Service Partner</label>
                  <select value={vendorType} onChange={e=>setVendorType(e.target.value)} style={ii}>
                    <option value="In-House Team">In-House Team</option>
                    <option value="Vendor List">Vendor List</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Priority + Due Date */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={lbl}>Priority *</label>
              <select value={priority} onChange={e=>setPriority(e.target.value)} style={ii}>
                {['low','medium','high','critical'].map(p=>(
                  <option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={lbl}>Due Date / Needed By *</label>
              <div style={{ display:'flex', gap:6 }}>
                <input type="date" value={dueDate} onChange={e=>setDueDate(e.target.value)}
                  style={{ ...ii, flex:1 }}/>
                {isBreakdown && (
                  <input type="time" value={dueTime} onChange={e=>setDueTime(e.target.value)}
                    style={{ ...ii, width:100, flexShrink:0 }}/>
                )}
              </div>
            </div>
          </div>

          {/* Assign To + Estimated Duration */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={lbl}>
                Assign To
                {assignTo && asset && (
                  String(asset?.assigned_to || asset?.responsible_person || '') === assignTo
                    ? <span style={{ fontSize:10, color:'#16a34a', marginLeft:6,
                        background:'#f0fdf4', padding:'1px 6px', borderRadius:4 }}>
                        ✓ From Asset
                      </span>
                    : null
                )}
              </label>
              <select value={assignTo} onChange={e=>setAssignTo(e.target.value)} style={ii}>
                <option value="">— Select / Auto-assign by skill —</option>
                {users.map(u=><option key={u.id} value={String(u.id)}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Estimated Duration</label>
              {isBreakdown ? (
                <select value={duration} onChange={e=>setDuration(e.target.value)} style={ii}>
                  <option value="">— Select Duration —</option>
                  <option value="< 2 Hours">&lt; 2 Hours</option>
                  <option value="2–4 Hours">2–4 Hours</option>
                  <option value="4–8 Hours">4–8 Hours</option>
                  <option value="1–3 Days">1–3 Days</option>
                  <option value="> 3 Days">&gt; 3 Days</option>
                </select>
              ) : (
                <input value={duration} onChange={e=>setDuration(e.target.value)}
                  placeholder="e.g. 2 hours" style={ii}/>
              )}
            </div>
          </div>

          {/* Estimated Cost */}
          <div>
            <label style={lbl}>Estimated Cost (₹)</label>
            <input type="number" value={cost} onChange={e=>setCost(e.target.value)}
              min="0" style={{ ...ii, width:180 }}/>
          </div>

          {/* Description */}
          <div>
            <label style={lbl}>Description / Problem Statement *</label>
            <textarea value={desc} onChange={e=>setDesc(e.target.value)}
              rows={4} placeholder="Describe the work needed or problem observed…"
              style={{ ...ii, resize:'vertical', fontFamily:'inherit' }}/>
          </div>

          {/* Checklist */}
          <div>
            <label style={lbl}>Checklist / Instructions</label>
            <textarea value={checklist} onChange={e=>setChecklist(e.target.value)}
              rows={3} placeholder={'1. Check oil levels\n2. Inspect seals\n3. Run diagnostic...'}
              style={{ ...ii, resize:'vertical', fontFamily:'inherit', fontSize:12 }}/>
          </div>

          {/* Spare Parts + Notify */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={lbl}>Spare Parts Required</label>
              <input value={spareParts} onChange={e=>setSpareParts(e.target.value)}
                placeholder="SKF-6205 x 2, FLT-CLC x 1" style={ii}/>
            </div>
           {/*  <div>
              <label style={lbl}>Notify</label>
              <select value={notify} onChange={e=>setNotify(e.target.value)} style={ii}>
                <option>Technician + Manager</option>
                <option>Technician Only</option>
                <option>Manager Only</option>
                <option>All Stakeholders</option>
              </select>
            </div> */}
          </div>
        </div>

        {/* ── Footer ── */}
        <div style={{ padding:'12px 20px', borderTop:'1px solid #e2e8f0',
          display:'flex', justifyContent:'flex-end', gap:10 }}>
          <button onClick={onClose}
            style={{ padding:'8px 18px', borderRadius:7, border:'1px solid #cbd5e1',
              background:'#fff', fontSize:13, cursor:'pointer', color:'#374151' }}>
            Cancel
          </button>
          <button onClick={submit} disabled={saving}
            style={{ padding:'8px 22px', borderRadius:7, border:'none',
              background:saving?'#93c5fd':'#1a56db', color:'#fff',
              fontSize:13, fontWeight:600, cursor:saving?'not-allowed':'pointer' }}>
            {saving ? 'Creating…' : '✓ Create Work Order'}
          </button>
        </div>
      </div>
    </div>
  );
}
